import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import "../../styles/MomentumRoutines.css";
import "../../styles/MomentumCommand.css";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useRunDetailPanel } from "../../context/RunDetailPanelContext";
import { useRoutinesStats } from "../../context/RoutinesStatsContext";
import { useInsuredDetail } from "../../context/InsuredDetailContext";
import { usePolicyDetail } from "../../context/PolicyDetailContext";
import { ROUTES } from "../../app/routes";
import { listDwAgents, type DwAgentRecord } from "../claudeagents/claudeAgentApi";
import { getRoutineStats } from "../../services/momentumRoutinesService";
import { processEntityLinks, stripTemplateMarkers } from "../chat/entityLinks";
import FeedbackEditorPopover from "../../components/ui/FeedbackEditorPopover";
import "../../styles/ChatMessage.css";
import { FiZap, FiClock, FiCalendar, FiRefreshCw, FiEdit3, FiTrash2, FiChevronLeft, FiChevronDown, FiCheck, FiAlertTriangle, FiBarChart2, FiAlertCircle, FiSearch, FiBookmark, FiUser, FiList, FiCopy, FiPaperclip, FiEye, FiX, FiGitBranch, FiMail, FiThumbsUp, FiThumbsDown, } from "react-icons/fi";
type ScheduleType = "daily" | "weekly" | "monthly" | "once" | "interval";
const SCHEDULE_TYPES: ScheduleType[] = ["daily", "weekly", "monthly", "once", "interval"];
// "interval" repeats every N minutes (momentum_routines.interval_minutes); the
// API computes next_run_at as now + N and the scheduler keeps it moving.
const INTERVAL_OPTIONS: { minutes: number; label: string }[] = [
    { minutes: 15, label: "15 min" },
    { minutes: 30, label: "30 min" },
    { minutes: 60, label: "1 hour" },
    { minutes: 120, label: "2 hours" },
    { minutes: 240, label: "4 hours" },
    { minutes: 360, label: "6 hours" },
    { minutes: 720, label: "12 hours" },
];
const DEFAULT_INTERVAL_MINUTES = 30;
// Routine template Library is hidden; set to true to show the Library button again.
const SHOW_ROUTINE_LIBRARY = false;
function intervalDuration(minutes?: number): string {
    const m = minutes && minutes > 0 ? minutes : DEFAULT_INTERVAL_MINUTES;
    if (m % 60 === 0)
        return m === 60 ? "1 hour" : `${m / 60} hours`;
    return `${m} minutes`;
}
function intervalLabel(minutes?: number): string {
    const m = minutes && minutes > 0 ? minutes : DEFAULT_INTERVAL_MINUTES;
    return m === 60 ? "Every hour" : `Every ${intervalDuration(m)}`;
}
const normalizeScheduleType = (value: unknown): ScheduleType => {
    const v = typeof value === "string" ? value.trim().toLowerCase() : "";
    return (SCHEDULE_TYPES as string[]).includes(v) ? (v as ScheduleType) : "daily";
};
interface Routine {
    id: string;
    name: string;
    description: string;
    prompt: string;
    skillIds: string[];
    skillNames: string[];
    assigneeAgentId?: string;
    assigneeAgentName?: string;
    scheduleType: ScheduleType;
    scheduleTime: string;
    scheduleTimeTz: string;
    scheduleDays: number[];
    scheduleDates: number[];
    scheduleOnceDate: string;
    intervalMinutes?: number;
    isActive: boolean;
    isSystem: boolean;
    lastRunAt?: string;
    nextRunAt?: string;
    createdAt: string;
}
interface RoutineTemplate {
    id: string;
    name: string;
    description: string;
    prompt: string;
    skillIds: string[];
    skillNames: string[];
    assigneeAgentId?: string;
    assigneeAgentName?: string;
    scheduleType: ScheduleType;
    scheduleTime: string;
    scheduleTimeTz: string;
    scheduleDays: number[];
    scheduleDates: number[];
    scheduleOnceDate: string;
    category?: string;
    icon?: string;
}
type RunStatus = "done" | "failed" | "running";
interface RunRecord {
    id: string;
    status: RunStatus;
    startedAt: string;
    finishedAt?: string;
    durationSec?: number;
    notes?: string;
    isLiked?: boolean;
    isDisliked?: boolean;
    feedback?: string;
}
interface ToolExecution {
    tool: string;
    action: string;
    result: string;
    status: string;
}
function generateMockHistory(routineId: string): RunRecord[] {
    const records: RunRecord[] = [];
    for (let i = 0; i < 10; i++) {
        const startedAt = new Date(Date.now() - i * 86400000 - Math.random() * 3600000);
        const isFirst = i === 0;
        const status: RunStatus = isFirst ? "done" : Math.random() > 0.2 ? "done" : "failed";
        const dur = Math.floor(30 + Math.random() * 120);
        records.push({
            id: `${routineId}-run-${i}`,
            status,
            startedAt: startedAt.toISOString(),
            finishedAt: new Date(startedAt.getTime() + dur * 1000).toISOString(),
            durationSec: dur,
            notes: status === "failed" ? "Connection timeout — skill API unreachable." : undefined,
        });
    }
    return records;
}
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function scheduleLabel(r: Routine): string {
    const time = formatTime(r.scheduleTime);
    if (r.scheduleType === "daily")
        return `Every day at ${time}`;
    if (r.scheduleType === "weekly") {
        const days = r.scheduleDays.map((d) => DAYS_OF_WEEK[d]).join(", ");
        return days ? `Every ${days} at ${time}` : `Weekly at ${time}`;
    }
    if (r.scheduleType === "monthly") {
        const dates = r.scheduleDates.map((d) => `${d}${ord(d)}`).join(", ");
        return dates ? `Monthly on ${dates} at ${time}` : `Monthly at ${time}`;
    }
    if (r.scheduleType === "once")
        return `Once on ${r.scheduleOnceDate} at ${time}`;
    if (r.scheduleType === "interval")
        return intervalLabel(r.intervalMinutes);
    return "";
}
function ord(n: number) {
    return n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
}
function formatTime(t: string): string {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}
function timeUntil(iso: string): string {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms < 0)
        return "Overdue";
    const h = Math.floor(ms / 3600000);
    if (h < 24)
        return h === 0 ? "Less than 1h" : `In ${h}h`;
    return `In ${Math.floor(h / 24)}d`;
}
function defaultRoutine(): Omit<Routine, "id" | "createdAt"> {
    return {
        name: "",
        description: "",
        prompt: "",
        skillIds: [],
        skillNames: [],
        assigneeAgentId: undefined,
        assigneeAgentName: undefined,
        scheduleType: "daily",
        scheduleTime: "09:00",
        scheduleDays: [],
        scheduleDates: [],
        scheduleOnceDate: "",
        intervalMinutes: DEFAULT_INTERVAL_MINUTES,
        scheduleTimeTz: "UTC",
        isActive: true,
        isSystem: false,
    };
}
const ToggleSwitch: React.FC<{
    on: boolean;
    onChange: () => void;
}> = ({ on, onChange }) => (<button type="button" onClick={onChange} className={`mr-toggle ${on ? "mr-toggle--on" : ""}`}>
    <div className={`mr-toggle__thumb ${on ? "mr-toggle__thumb--on" : ""}`}/>
  </button>);
const RUN_STATUS_CFG: Record<RunStatus, {
    label: string;
    color: string;
    bg: string;
    border: string;
    Icon: React.ElementType;
}> = {
    done: { label: "Done", color: "#34d399", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)", Icon: FiCheck },
    failed: { label: "Failed", color: "#f87171", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.28)", Icon: FiAlertTriangle },
    running: { label: "Running", color: "#fbbf24", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.3)", Icon: FiRefreshCw },
};
function fmtDatetime(iso: string): string {
    const d = new Date(iso);
    return (d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " · " +
        d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }));
}
function fmtDuration(sec?: number): string {
    if (!sec)
        return "—";
    return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
}
const HISTORY_PAGE_SIZE = 10;
function mapRunRecord(r: any): RunRecord {
    const validStatuses: RunStatus[] = ["done", "failed", "running"];
    return {
        id: String(r.id),
        status: validStatuses.includes(r.status) ? (r.status as RunStatus) : "done",
        startedAt: r.startedAt ?? new Date().toISOString(),
        finishedAt: r.finishedAt ?? undefined,
        durationSec: r.durationSec ?? undefined,
        notes: r.notes ?? undefined,
        isLiked: r.isLiked ?? r.IsLiked ?? false,
        isDisliked: r.isDisliked ?? r.IsDisliked ?? false,
        feedback: r.feedback ?? r.Feedback ?? '',
    };
}
function mergeRunRecords(prev: RunRecord[], fresh: RunRecord[]): RunRecord[] {
    const existingIds = new Set(prev.map((r) => r.id));
    const byId = new Map(fresh.map((r) => [r.id, r]));
    const updated = prev.map((r) => byId.get(r.id) ?? r);
    const brandNew = fresh.filter((r) => !existingIds.has(r.id));
    return [...brandNew, ...updated];
}
const RoutineDetailPage: React.FC<{
    routine: Routine;
    onBack: () => void;
    onEdit: () => void;
    autoExpandRunId?: string | null;
}> = ({ routine, onBack, onEdit, autoExpandRunId }) => {
    const { theme } = useTheme();
    const isLight = theme === "light";
    const { setRunDetailPanel, runDetailPanel } = useRunDetailPanel();
    const { setSelectedInsuredId } = useInsuredDetail();
    const { setSelectedPolicyId } = usePolicyDetail();
    const [history, setHistory] = useState<RunRecord[]>([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [executionsMap, setExecutionsMap] = useState<Record<string, ToolExecution[]>>({});
    const [runNowLoading, setRunNowLoading] = useState(false);
    const [runNowResult, setRunNowResult] = useState<"success" | "error" | null>(null);
    const [runNowTooltip, setRunNowTooltip] = useState(false);
    const [runNowTooltipPos, setRunNowTooltipPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const runNowRef = useRef<HTMLDivElement>(null);
    const [historyRefreshing, setHistoryRefreshing] = useState(false);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(autoExpandRunId ? new Set([autoExpandRunId]) : new Set());
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
    const historySentinelRef = useRef<HTMLDivElement>(null);
    const [feedbackEditorRunId, setFeedbackEditorRunId] = useState<string | null>(null);
    const [feedbackEditorPos, setFeedbackEditorPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const [feedbackDraft, setFeedbackDraft] = useState("");
    useEffect(() => () => {
        setRunDetailPanel(null);
    }, [setRunDetailPanel]);
    const fetchHistory = useCallback((isManual = false) => {
        if (isManual)
            setHistoryRefreshing(true);
        else
            setHistoryLoading(true);
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${routine.id}/history?limit=${HISTORY_PAGE_SIZE}&offset=0`, { headers: routineHeaders() })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            const total: number = json?.totalCount ?? list.length;
            setHistory(list.map(mapRunRecord));
            setHistoryTotal(total);
            setHistoryPage(1);
            setHasMoreHistory(list.length < total);
        })
            .catch(() => {
            if (!isManual)
                setHistory(generateMockHistory(routine.id));
        })
            .finally(() => {
            setHistoryLoading(false);
            setHistoryRefreshing(false);
        });
    }, [routine.id]);
    const loadMoreHistory = useCallback(() => {
        const nextPage = historyPage + 1;
        setLoadingMoreHistory(true);
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${routine.id}/history?limit=${HISTORY_PAGE_SIZE}&offset=${(nextPage - 1) * HISTORY_PAGE_SIZE}`, { headers: routineHeaders() })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            const total: number = json?.totalCount ?? historyTotal;
            setHistory((prev) => [...prev, ...list.map(mapRunRecord)]);
            setHistoryTotal(total);
            setHistoryPage(nextPage);
            setHasMoreHistory(nextPage * HISTORY_PAGE_SIZE < total);
        })
            .catch(() => { })
            .finally(() => setLoadingMoreHistory(false));
    }, [routine.id, historyPage, historyTotal]);
    const handleRunFeedback = useCallback((runId: string, patch: {
        isLiked?: boolean;
        isDisliked?: boolean;
        feedback?: string;
    }) => {
        setHistory((prev) => prev.map((r) => r.id === runId
            ? {
                ...r,
                isLiked: patch.isLiked ?? r.isLiked ?? false,
                isDisliked: patch.isDisliked ?? r.isDisliked ?? false,
                feedback: patch.feedback ?? r.feedback ?? "",
            }
            : r));
        const target = history.find((r) => r.id === runId);
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/history/${runId}/feedback`, {
            method: "PATCH",
            headers: routineHeaders(),
            body: JSON.stringify({
                routineId: routine.id,
                isLiked: patch.isLiked ?? target?.isLiked ?? false,
                isDisliked: patch.isDisliked ?? target?.isDisliked ?? false,
                feedback: patch.feedback ?? target?.feedback ?? "",
            }),
        }).catch(() => { });
    }, [history, routine.id]);
    const handleToggleRunLike = useCallback((runId: string) => {
        const r = history.find((h) => h.id === runId);
        if (!r)
            return;
        handleRunFeedback(runId, { isLiked: !r.isLiked, isDisliked: false });
    }, [history, handleRunFeedback]);
    const handleToggleRunDislike = useCallback((runId: string) => {
        const r = history.find((h) => h.id === runId);
        if (!r)
            return;
        handleRunFeedback(runId, { isDisliked: !r.isDisliked, isLiked: false });
    }, [history, handleRunFeedback]);
    const handleOpenRunFeedbackEditor = useCallback((runId: string, e: React.MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const popupWidth = 360;
        const popupHeight = 140;
        const padding = 16;
        let top = rect.bottom + 6;
        let left = rect.left;
        if (left + popupWidth > window.innerWidth)
            left = window.innerWidth - popupWidth - padding;
        if (left < padding)
            left = padding;
        if (top + popupHeight > window.innerHeight)
            top = rect.top - popupHeight - 6;
        if (top < padding)
            top = padding;
        setFeedbackDraft(history.find((h) => h.id === runId)?.feedback || "");
        setFeedbackEditorPos({ top, left });
        setFeedbackEditorRunId(runId);
    }, [history]);
    const closeRunFeedbackEditor = useCallback(() => {
        setFeedbackEditorRunId(null);
        setFeedbackEditorPos(null);
    }, []);
    const handleSaveRunFeedback = useCallback(() => {
        if (!feedbackEditorRunId)
            return;
        handleRunFeedback(feedbackEditorRunId, { feedback: feedbackDraft });
        closeRunFeedbackEditor();
    }, [feedbackEditorRunId, feedbackDraft, handleRunFeedback, closeRunFeedbackEditor]);
    const handleRunNow = async () => {
        setRunNowLoading(true);
        setRunNowResult(null);
        try {
            const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/momentum-routine`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify({ routineId: routine.id }),
            });
            if (!res.ok)
                throw new Error("Failed");
            setRunNowResult("success");
            setTimeout(() => fetchHistory(), 1500);
        }
        catch {
            setRunNowResult("error");
        }
        finally {
            setRunNowLoading(false);
            setTimeout(() => setRunNowResult(null), 3000);
        }
    };
    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);
    const hasRunning = history.some((r) => r.status === "running");
    useEffect(() => {
        if (!hasRunning)
            return;
        const timer = setInterval(() => {
            fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${routine.id}/history?limit=${HISTORY_PAGE_SIZE}&offset=0`, { headers: routineHeaders() })
                .then((r) => (r.ok ? r.json() : Promise.reject()))
                .then((json) => {
                const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
                setHistory((prev) => mergeRunRecords(prev, list.map(mapRunRecord)));
            })
                .catch(() => { });
        }, 5000);
        return () => clearInterval(timer);
    }, [hasRunning, routine.id]);
    useEffect(() => {
        const el = historySentinelRef.current;
        if (!el || !hasMoreHistory || loadingMoreHistory)
            return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting)
                loadMoreHistory();
        }, { threshold: 0.1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMoreHistory, loadingMoreHistory, loadMoreHistory]);
    useEffect(() => {
        if (!runDetailPanel)
            return;
        const fresh = history.find((r) => r.id === runDetailPanel.run.id);
        if (!fresh)
            return;
        if (fresh.status !== runDetailPanel.run.status ||
            fresh.durationSec !== runDetailPanel.run.durationSec ||
            fresh.finishedAt !== runDetailPanel.run.finishedAt) {
            setRunDetailPanel((prev) => prev?.run.id === fresh.id ? { ...prev, run: { ...prev.run, ...fresh } } : prev);
        }
    }, [history, runDetailPanel, setRunDetailPanel]);
    useEffect(() => {
        if (!runDetailPanel || runDetailPanel.run.status !== "running")
            return;
        const runId = runDetailPanel.run.id;
        const timer = setInterval(() => {
            fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/history/${runId}/executions`, { headers: routineHeaders() })
                .then((r) => (r.ok ? r.json() : Promise.reject()))
                .then((json) => {
                const execs: ToolExecution[] = Array.isArray(json) ? json : (json?.data?.tool_executions ?? json?.tool_executions ?? []);
                setExecutionsMap((prev) => ({ ...prev, [runId]: execs }));
                setRunDetailPanel((prev) => (prev?.run.id === runId ? { ...prev, execs, exeLoading: false } : prev));
            })
                .catch(() => { });
        }, 4000);
        return () => clearInterval(timer);
    }, [runDetailPanel?.run.id, runDetailPanel?.run.status, setRunDetailPanel]);
    const handleViewDetails = (runId: string) => {
        const run = history.find((r) => r.id === runId);
        if (!run)
            return;
        setSelectedInsuredId(null);
        setSelectedPolicyId(null);
        setRunDetailPanel({ run, execs: null, exeLoading: true, kind: 'routine' });
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/history/${runId}/executions`, { headers: routineHeaders() })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            const execs: ToolExecution[] = Array.isArray(json) ? json : (json?.data?.tool_executions ?? json?.tool_executions ?? []);
            setExecutionsMap((prev) => ({ ...prev, [runId]: execs }));
            setRunDetailPanel((prev) => (prev?.run.id === runId ? { ...prev, execs, exeLoading: false } : prev));
        })
            .catch(() => {
            setExecutionsMap((prev) => ({ ...prev, [runId]: [] }));
            setRunDetailPanel((prev) => (prev?.run.id === runId ? { ...prev, execs: [], exeLoading: false } : prev));
        });
    };
    const doneCount = history.filter((r) => r.status === "done").length;
    const failedCount = history.filter((r) => r.status === "failed").length;
    const successRate = history.length > 0 ? Math.round((doneCount / history.length) * 100) : 0;
    const lastRun = history[0];
    useEffect(() => {
        if (!lastRun)
            return;
        handleViewDetails(lastRun.id);
    }, [lastRun?.id]);
    return (<motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
      
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 24 }}>
        <button onClick={() => {
            setRunDetailPanel(null);
            onBack();
        }} className="mr-detail-header__back">
          <FiChevronLeft size={15}/> Back to Routines
        </button>
        <button onClick={onEdit} style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 9,
            border: isLight ? "1px solid #d1d5db" : "1px solid rgba(255,255,255,0.15)",
            background: isLight ? "#ffffff" : "rgba(255,255,255,0.05)",
            color: isLight ? "#374151" : "rgba(255,255,255,0.75)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            boxShadow: isLight ? "0 1px 3px rgba(15,23,42,0.06)" : "none",
        }}>
          <FiEdit3 size={13}/> Edit Routine
        </button>
      </div>

      
      <div style={{
            background: isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
            border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
            borderRadius: 16,
            padding: "20px 24px",
            marginBottom: 20,
            boxShadow: isLight ? "0 4px 14px rgba(15,23,42,0.05)" : "none",
        }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, justifyContent: "space-between" }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            flexShrink: 0,
            background: routine.isActive ? "rgba(124,58,237,0.15)" : "rgba(255,255,255,0.05)",
            border: `1.5px solid ${routine.isActive ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.1)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: routine.isActive ? "#a78bfa" : "rgba(255,255,255,0.3)",
        }}>
            <FiRefreshCw size={20}/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
              <h1 style={{ color: isLight ? "#111827" : "rgba(255,255,255,0.92)", fontSize: 18, fontWeight: 700, margin: 0 }}>{routine.name}</h1>
              <span style={{
            padding: "3px 10px",
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 600,
            background: routine.isActive ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.06)",
            border: `1px solid ${routine.isActive ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.1)"}`,
            color: routine.isActive ? "#34d399" : "rgba(255,255,255,0.35)",
        }}>
                {routine.isActive ? "● Active" : "○ Paused"}
              </span>
              {routine.skillNames.map((n) => (<span key={n} style={{
                background: "rgba(124,58,237,0.12)",
                border: "1px solid rgba(124,58,237,0.28)",
                color: "#c4b5fd",
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 99,
                display: "flex",
                alignItems: "center",
                gap: 4,
            }}>
                  <FiZap size={10}/> {n}
                </span>))}
            </div>
            {routine.description && (<p style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.42)", fontSize: 13, margin: "0 0 8px", lineHeight: 1.6 }}>
                {routine.description}
              </p>)}
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span className="mr-routine-card__schedule">
                <FiClock size={11}/> {scheduleLabel(routine)}
              </span>
              <span className="mr-routine-card__assignee" title="Assigned agent">
                <FiUser size={11}/> {routine.assigneeAgentName || "AI Agent"}
              </span>
            </div>
          </div>
          <div ref={runNowRef} style={{ position: "relative", flexShrink: 0, alignSelf: "flex-start" }} onMouseEnter={() => {
            if (!routine.isActive && runNowRef.current) {
                const r = runNowRef.current.getBoundingClientRect();
                setRunNowTooltipPos({ top: r.top - 8, left: r.left + r.width / 2 });
                setRunNowTooltip(true);
            }
        }} onMouseLeave={() => {
            setRunNowTooltip(false);
            setRunNowTooltipPos(null);
        }}>
            <button onClick={handleRunNow} disabled={runNowLoading || !routine.isActive} style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 9,
            border: "none",
            background: runNowResult === "success"
                ? "linear-gradient(135deg, #059669, #047857)"
                : runNowResult === "error"
                    ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                    : "linear-gradient(135deg, #7c3aed, #6d28d9)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: runNowLoading || !routine.isActive ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            opacity: runNowLoading || !routine.isActive ? 0.5 : 1,
            transition: "background 0.2s, opacity 0.2s",
        }}>
              {runNowLoading ? (<FiRefreshCw size={13} style={{ animation: "spin 1s linear infinite" }}/>) : runNowResult === "success" ? (<FiCheck size={13}/>) : runNowResult === "error" ? (<FiAlertCircle size={13}/>) : (<FiZap size={13}/>)}
              {runNowLoading ? "Running…" : runNowResult === "success" ? "Triggered!" : runNowResult === "error" ? "Failed" : "Run Now"}
            </button>
          </div>
          {runNowTooltip &&
            runNowTooltipPos &&
            ReactDOM.createPortal(<div style={{
                    position: "fixed",
                    top: runNowTooltipPos.top,
                    left: runNowTooltipPos.left,
                    transform: "translate(-50%, -100%)",
                    background: "#1e1533",
                    border: "1px solid rgba(124,58,237,0.35)",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 12,
                    color: "rgba(255,255,255,0.85)",
                    whiteSpace: "nowrap",
                    zIndex: 99999,
                    pointerEvents: "none",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                }}>
                Routine is inactive, please activate to use it
                <div style={{
                    position: "absolute",
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderTop: "5px solid rgba(124,58,237,0.35)",
                }}/>
              </div>, document.body)}
        </div>
      </div>

      
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
            {
                label: "Last Run",
                value: lastRun ? fmtDatetime(lastRun.startedAt).split(" · ")[0] : "—",
                sub: lastRun ? fmtDatetime(lastRun.startedAt).split(" · ")[1] : "",
                color: "#8b5cf6",
            },
            {
                label: "Next Run",
                value: routine.nextRunAt && routine.isActive ? timeUntil(routine.nextRunAt) : "—",
                sub: routine.nextRunAt && routine.isActive ? fmtDatetime(routine.nextRunAt).split(" · ")[0] : "Paused",
                color: "#22d3ee",
            },
            {
                label: "Success Rate",
                value: `${successRate}%`,
                sub: `${doneCount} done · ${failedCount} failed`,
                color: successRate >= 80 ? "#34d399" : successRate >= 50 ? "#fbbf24" : "#f87171",
            },
            { label: "Total Runs", value: String(historyTotal || history.length), sub: `${history.length} loaded`, color: "#f59e0b" },
        ].map((s) => (<div key={s.label} style={{
                background: isLight ? "#ffffff" : "rgba(0,0,0,0.25)",
                border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.07)",
                borderRadius: 12,
                padding: "14px 18px",
            }}>
            <div style={{
                color: isLight ? "#6b7280" : "rgba(255,255,255,0.35)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                marginBottom: 6,
            }}>
              {s.label}
            </div>
            <div style={{ color: s.color, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
            {s.sub && <div style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.3)", fontSize: 11, marginTop: 4 }}>{s.sub}</div>}
          </div>))}
      </div>

      
      <div style={{
            background: isLight ? "#ffffff" : "rgba(0,0,0,0.2)",
            border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.07)",
            borderRadius: 16,
            overflow: "hidden",
        }}>
        
        <div style={{
            padding: "12px 20px",
            borderBottom: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 8,
        }}>
          <FiBarChart2 size={14} style={{ color: "#a78bfa" }}/>
          <span style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600 }}>Run History</span>
          <span style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.25)", fontSize: 12, marginLeft: 4 }}>
            {history.length} of {historyTotal || history.length}
          </span>
          <button onClick={() => fetchHistory(true)} disabled={historyRefreshing} title="Refresh history" style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: 7,
            border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.1)",
            background: "transparent",
            color: isLight ? "#6b7280" : "rgba(255,255,255,0.45)",
            fontSize: 12,
            fontWeight: 500,
            cursor: historyRefreshing ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            opacity: historyRefreshing ? 0.6 : 1,
        }}>
            <FiRefreshCw size={12} style={historyRefreshing ? { animation: "spin 1s linear infinite" } : undefined}/>
            {historyRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        
        {!historyLoading && history.length > 0 && (<div className="mr-history__streak">
            <span className="mr-history__streak-label">Streak</span>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              {[...history].reverse().map((r) => {
                const dotColor = r.status === "done" ? "#34d399" : r.status === "failed" ? "#f87171" : "#fbbf24";
                return (<div key={r.id} title={`${r.status} · ${fmtDatetime(r.startedAt)}`} style={{ width: 9, height: 9, borderRadius: "50%", background: dotColor, opacity: 0.85, flexShrink: 0, cursor: "default" }}/>);
            })}
            </div>
            <span className="mr-history__streak-rate">{successRate}% success</span>
          </div>)}

        
        <div style={{
            display: "grid",
            gridTemplateColumns: "104px 1fr 80px 72px",
            padding: "8px 20px",
            borderBottom: isLight ? "1px solid #dbe4f0" : "1px solid rgba(255,255,255,0.1)",
        }}>
          {["Status", "Started At", "Duration", ""].map((h) => (<span key={h} style={{
                color: isLight ? "#9ca3af" : "rgba(255,255,255,0.28)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
            }}>
              {h}
            </span>))}
        </div>

        
        {historyLoading ? (<div style={{ padding: "28px 20px", textAlign: "center", color: isLight ? "#6b7280" : "rgba(255,255,255,0.25)", fontSize: 13 }}>
            Loading history…
          </div>) : history.length === 0 ? (<div style={{ padding: "28px 20px", textAlign: "center", color: isLight ? "#6b7280" : "rgba(255,255,255,0.2)", fontSize: 13 }}>
            No runs yet. This routine hasn't executed yet.
          </div>) : null}
        {!historyLoading &&
            history.map((run, idx) => {
                const cfg = RUN_STATUS_CFG[run.status] ?? RUN_STATUS_CFG["done"];
                const isRowExpanded = expandedRows.has(run.id);
                const toggleRow = () => setExpandedRows((prev) => {
                    const next = new Set(prev);
                    if (next.has(run.id))
                        next.delete(run.id);
                    else
                        next.add(run.id);
                    return next;
                });
                return (<div key={run.id} style={{ borderBottom: idx < history.length - 1 ? (isLight ? "1px solid #d9e2ef" : "1px solid rgba(255,255,255,0.1)") : "none" }}>
                
                <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.04 }} whileHover={{ background: isLight ? "rgba(124,58,237,0.04)" : "rgba(124,58,237,0.07)" }} onClick={toggleRow} style={{
                        display: "grid",
                        gridTemplateColumns: "104px 1fr 80px 32px",
                        padding: "12px 20px",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        background: idx === 0 && !isRowExpanded ? (isLight ? "#f8fafc" : "rgba(255,255,255,0.02)") : "transparent",
                    }}>
                  
                  <div>
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 9px",
                        borderRadius: 99,
                        fontSize: 11,
                        fontWeight: 600,
                        background: cfg.bg,
                        border: `1px solid ${cfg.border}`,
                        color: cfg.color,
                    }}>
                      <cfg.Icon size={10}/> {cfg.label}
                    </span>
                  </div>

                  
                  <div>
                    <div style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.8)", fontSize: 13, fontWeight: 500 }}>
                      {fmtDatetime(run.startedAt).split(" · ")[0]}
                    </div>
                    <div style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.28)", fontSize: 11, marginTop: 1 }}>
                      {fmtDatetime(run.startedAt).split(" · ")[1]}
                    </div>
                  </div>

                  
                  <div style={{ color: isLight ? "#4b5563" : "rgba(255,255,255,0.55)", fontSize: 13 }}>{fmtDuration(run.durationSec)}</div>

                  
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <FiChevronDown size={15} style={{
                        color: isRowExpanded ? "#a78bfa" : isLight ? "#d1d5db" : "rgba(255,255,255,0.22)",
                        transition: "transform 200ms, color 150ms",
                        transform: isRowExpanded ? "rotate(180deg)" : "none",
                    }}/>
                  </div>
                </motion.div>

                
                <AnimatePresence>
                  {isRowExpanded && (<motion.div key={`expand-${run.id}`} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }} style={{ overflow: "hidden" }}>
                      <div style={{
                            padding: "10px 20px 14px 20px",
                            background: isLight ? "#fafbff" : "rgba(124,58,237,0.03)",
                            borderTop: `1px solid ${isLight ? "#eef2f7" : "rgba(124,58,237,0.1)"}`,
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}>
                        
                        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                          {run.finishedAt && (<div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <FiClock size={11} style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.28)", flexShrink: 0 }}/>
                              <span style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.42)", fontSize: 12 }}>
                                Finished:&nbsp;
                                <span style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.65)", fontWeight: 500 }}>
                                  {fmtDatetime(run.finishedAt).split(" · ")[1]}
                                </span>
                              </span>
                            </div>)}
                          {run.durationSec != null && (<div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <FiRefreshCw size={11} style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.28)", flexShrink: 0 }}/>
                              <span style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.42)", fontSize: 12 }}>
                                Duration:&nbsp;
                                <span style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.65)", fontWeight: 500 }}>
                                  {fmtDuration(run.durationSec)}
                                </span>
                              </span>
                            </div>)}
                          <div style={{ flex: 1 }}/>
                          <button onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(run.id);
                        }} style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "5px 11px",
                            borderRadius: 7,
                            border: "none",
                            background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            whiteSpace: "nowrap",
                            boxShadow: "0 1px 6px rgba(124,58,237,0.28)",
                            flexShrink: 0,
                        }}>
                            <FiBarChart2 size={11}/> View Details
                          </button>
                        </div>

                        
                        {run.notes &&
                            (run.status === "failed" ? (<div style={{
                                    padding: "7px 10px",
                                    background: isLight ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.1)",
                                    border: `1px solid ${isLight ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.22)"}`,
                                    borderRadius: 8,
                                    color: "#f87171",
                                    fontSize: 11,
                                    lineHeight: 1.5,
                                    display: "flex",
                                    gap: 6,
                                    alignItems: "flex-start",
                                }}>
                              <FiAlertTriangle size={11} style={{ flexShrink: 0, marginTop: 1 }}/>
                              <span>{run.notes}</span>
                            </div>) : (<div style={{
                                    padding: "12px 14px",
                                    background: isLight ? "#ffffff" : "rgba(255,255,255,0.03)",
                                    border: `1px solid ${isLight ? "#e2e8f0" : "rgba(255,255,255,0.07)"}`,
                                    borderRadius: 10,
                                    maxHeight: 420,
                                    overflowY: "auto",
                                    width: "100%",
                                    boxSizing: "border-box",
                                }}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                    a: ({ href, children, ...props }: any) => {
                                        if (href?.startsWith("#INSURED_ID_")) {
                                            const insuredId = href.replace("#INSURED_ID_", "");
                                            return (<button className="insured-link" onClick={() => { setSelectedPolicyId(null); setSelectedInsuredId(insuredId); }}>
                                          {children}
                                        </button>);
                                        }
                                        if (href?.startsWith("#POLICY_ID_")) {
                                            const policyId = href.replace("#POLICY_ID_", "");
                                            return (<button className="insured-link insured-link--policy" onClick={() => { setSelectedInsuredId(null); setSelectedPolicyId(policyId); }}>
                                          {children}
                                        </button>);
                                        }
                                        return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
                                    },
                                    h1: ({ children }: any) => (<h1 style={{
                                            fontSize: 14,
                                            fontWeight: 800,
                                            color: isLight ? "#0f172a" : "rgba(255,255,255,0.9)",
                                            margin: "0 0 8px",
                                            paddingBottom: 6,
                                            borderBottom: `1px solid ${isLight ? "#e5e7eb" : "rgba(255,255,255,0.08)"}`,
                                        }}>
                                      {children}
                                    </h1>),
                                    h2: ({ children }: any) => (<h2 style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: isLight ? "#111827" : "rgba(255,255,255,0.85)",
                                            margin: "12px 0 5px",
                                        }}>
                                      {children}
                                    </h2>),
                                    h3: ({ children }: any) => (<h3 style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            color: isLight ? "#374151" : "rgba(255,255,255,0.78)",
                                            margin: "10px 0 4px",
                                        }}>
                                      {children}
                                    </h3>),
                                    p: ({ children }: any) => (<p style={{
                                            fontSize: 11.5,
                                            color: isLight ? "#374151" : "rgba(255,255,255,0.7)",
                                            margin: "3px 0",
                                            lineHeight: 1.6,
                                        }}>
                                      {children}
                                    </p>),
                                    ul: ({ children }: any) => <ul style={{ margin: "5px 0", paddingLeft: 18 }}>{children}</ul>,
                                    ol: ({ children }: any) => <ol style={{ margin: "5px 0", paddingLeft: 18 }}>{children}</ol>,
                                    li: ({ children }: any) => (<li style={{
                                            fontSize: 11.5,
                                            color: isLight ? "#374151" : "rgba(255,255,255,0.68)",
                                            margin: "2px 0",
                                            lineHeight: 1.5,
                                        }}>
                                      {children}
                                    </li>),
                                    strong: ({ children }: any) => (<strong style={{ fontWeight: 700, color: isLight ? "#111827" : "rgba(255,255,255,0.88)" }}>{children}</strong>),
                                    hr: () => (<hr style={{
                                            border: "none",
                                            borderTop: `1px solid ${isLight ? "#e5e7eb" : "rgba(255,255,255,0.07)"}`,
                                            margin: "10px 0",
                                        }}/>),
                                    table: ({ children }: any) => (<div style={{ overflowX: "auto", margin: "8px 0" }}>
                                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>{children}</table>
                                    </div>),
                                    thead: ({ children }: any) => (<thead style={{ background: isLight ? "#f1f5f9" : "rgba(255,255,255,0.05)" }}>{children}</thead>),
                                    tbody: ({ children }: any) => <tbody>{children}</tbody>,
                                    tr: ({ children }: any) => (<tr style={{ borderBottom: `1px solid ${isLight ? "#e5e7eb" : "rgba(255,255,255,0.06)"}` }}>{children}</tr>),
                                    th: ({ children }: any) => (<th style={{
                                            padding: "5px 9px",
                                            textAlign: "left",
                                            fontWeight: 700,
                                            color: isLight ? "#374151" : "rgba(255,255,255,0.6)",
                                            whiteSpace: "nowrap",
                                            fontSize: 10,
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                        }}>
                                      {children}
                                    </th>),
                                    td: ({ children }: any) => (<td style={{
                                            padding: "5px 9px",
                                            color: isLight ? "#374151" : "rgba(255,255,255,0.68)",
                                            verticalAlign: "top",
                                            fontSize: 11,
                                        }}>
                                      {children}
                                    </td>),
                                    code: ({ children }: any) => (<code style={{
                                            background: isLight ? "#f1f5f9" : "rgba(0,0,0,0.2)",
                                            padding: "1px 5px",
                                            borderRadius: 3,
                                            fontFamily: "monospace",
                                            fontSize: 10.5,
                                            color: isLight ? "#4b5563" : "rgba(255,255,255,0.6)",
                                        }}>
                                      {children}
                                    </code>),
                                    blockquote: ({ children }: any) => (<blockquote style={{
                                            borderLeft: `3px solid ${isLight ? "#a78bfa" : "rgba(167,139,250,0.4)"}`,
                                            margin: "6px 0",
                                            paddingLeft: 10,
                                            color: isLight ? "#6b7280" : "rgba(255,255,255,0.5)",
                                            fontStyle: "italic",
                                        }}>
                                      {children}
                                    </blockquote>),
                                }}>
                                {stripTemplateMarkers(processEntityLinks(run.notes))}
                              </ReactMarkdown>
                            </div>))}

                        
                        {run.status !== "running" && (<div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => handleToggleRunLike(run.id)} title={run.isLiked ? "Helpful - Click to remove" : "Mark as helpful"} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: run.isLiked ? "#10b981" : isLight ? "#9ca3af" : "rgba(255,255,255,0.35)" }}>
                              <FiThumbsUp size={12}/>
                            </button>
                            <button onClick={() => handleToggleRunDislike(run.id)} title={run.isDisliked ? "Not helpful - Click to remove" : "Mark as not helpful"} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: run.isDisliked ? "#ef4444" : isLight ? "#9ca3af" : "rgba(255,255,255,0.35)" }}>
                              <FiThumbsDown size={12}/>
                            </button>
                            <button onClick={(e) => handleOpenRunFeedbackEditor(run.id, e)} title={run.feedback ? "Edit feedback" : "Add feedback"} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: run.feedback ? "#a78bfa" : isLight ? "#9ca3af" : "rgba(255,255,255,0.35)" }}>
                              <FiEdit3 size={12}/>
                            </button>
                          </div>)}
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div>);
            })}

        
        <FeedbackEditorPopover open={feedbackEditorRunId !== null && !!feedbackEditorPos} position={feedbackEditorPos} value={feedbackDraft} onChange={setFeedbackDraft} onClose={closeRunFeedbackEditor} onSave={handleSaveRunFeedback} title="Edit Feedback" placeholder="Add your feedback here..." borderColor={isLight ? "#e2e8f0" : "rgba(255,255,255,0.1)"} backgroundColor={isLight ? "#ffffff" : "#1a1a2e"} titleColor={isLight ? "#111827" : "rgba(255,255,255,0.9)"} cancelColor={isLight ? "#6b7280" : "rgba(255,255,255,0.6)"} cancelBorderColor={isLight ? "#e2e8f0" : "rgba(255,255,255,0.1)"} saveColor="#a78bfa" saveBorderColor="rgba(139,92,246,0.4)" saveBackgroundColor="rgba(139,92,246,0.16)"/>
        {!historyLoading && hasMoreHistory && (<div ref={historySentinelRef} style={{ height: 1 }}/>)}
        {!historyLoading && loadingMoreHistory && (<div style={{
                display: "flex",
                justifyContent: "center",
                padding: "14px 0",
                color: isLight ? "#9ca3af" : "rgba(255,255,255,0.3)",
                fontSize: 12,
                gap: 8,
                alignItems: "center",
            }}>
            <FiRefreshCw size={12} style={{ animation: "spin 1s linear infinite" }}/>
            Loading more…
          </div>)}
      </div>
    </motion.div>);
};
const RoutineCard: React.FC<{
    routine: Routine;
    onView: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onToggle: () => void;
}> = ({ routine, onView, onEdit, onDelete, onToggle, }) => (<motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.18 }} onClick={onView} whileHover={{ scale: 1.005, y: -1 }} className="mr-routine-card" style={{ borderLeft: `3px solid ${routine.isActive ? "#7c3aed" : "currentColor"}` }}>
    <div style={{
        width: 42,
        height: 42,
        borderRadius: 11,
        flexShrink: 0,
        background: routine.isActive ? "rgba(124,58,237,0.15)" : "rgba(255,255,255,0.04)",
        border: `1.5px solid ${routine.isActive ? "rgba(124,58,237,0.4)" : "rgba(255,255,255,0.08)"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: routine.isActive ? "#a78bfa" : "rgba(255,255,255,0.3)",
    }}>
      <FiRefreshCw size={17}/>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 className="mr-routine-card__title" style={{ margin: 0 }}>
          {routine.name}
        </h3>
        {routine.isSystem && (<span style={{
            background: "rgba(162,0,255,0.12)",
            border: "1px solid rgba(162,0,255,0.3)",
            color: "#c084fc",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 99,
            letterSpacing: "0.03em",
            textTransform: "uppercase" as const,
        }}>
            System
          </span>)}
        {routine.skillNames.map((n) => (<span key={n} style={{
            background: "rgba(124,58,237,0.12)",
            border: "1px solid rgba(124,58,237,0.28)",
            color: "#c4b5fd",
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 7px",
            borderRadius: 99,
            display: "flex",
            alignItems: "center",
            gap: 3,
        }}>
            <FiZap size={9}/> {n}
          </span>))}
      </div>
      {routine.description && (<p className="mr-routine-card__description" style={{ margin: "0 0 10px" }}>
          {routine.description}
        </p>)}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }} className="mr-routine-card__schedule">
          <FiClock size={11}/> {scheduleLabel(routine)}
        </span>
        <span className="mr-routine-card__assignee" title="Assigned agent">
          <FiUser size={11}/> {routine.assigneeAgentName || "AI Agent"}
        </span>
        {routine.nextRunAt && routine.isActive && (<span className="mr-routine-card__next-run">
            <FiCalendar size={11}/> Next: {timeUntil(routine.nextRunAt)}
          </span>)}
        {!routine.isActive && <span className="mr-routine-card__status">Paused</span>}
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <ToggleSwitch on={routine.isActive} onChange={onToggle}/>
        <button onClick={routine.isSystem ? undefined : onEdit} className="mr-routine-card__btn" disabled={routine.isSystem} title={routine.isSystem ? "System routines cannot be edited" : "Edit routine"} style={{ opacity: routine.isSystem ? 0.35 : 1, cursor: routine.isSystem ? "not-allowed" : "pointer" }}>
          <FiEdit3 size={12}/> 
        </button>
        <button onClick={routine.isSystem ? undefined : onDelete} className="mr-routine-card__delete-btn" disabled={routine.isSystem} title={routine.isSystem ? "System routines cannot be deleted" : "Delete routine"} style={{ opacity: routine.isSystem ? 0.35 : 1, cursor: routine.isSystem ? "not-allowed" : "pointer" }}>
          <FiTrash2 size={13}/>
        </button>
      </div>
    </div>
  </motion.div>);
const inputStyle = (isLight: boolean): React.CSSProperties => ({
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13,
    background: isLight ? "#ffffff" : "rgba(255,255,255,0.05)",
    border: `1px solid ${isLight ? "#d1d5db" : "rgba(255,255,255,0.1)"}`,
    color: isLight ? "#111827" : "rgba(255,255,255,0.85)",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    colorScheme: isLight ? "light" : "dark",
});
const FormLabel: React.FC<{
    children: React.ReactNode;
    isLight?: boolean;
}> = ({ children, isLight = false }) => (<label className="mr-form-label">{children}</label>);
const FormSection: React.FC<{
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    isLight?: boolean;
    step?: number;
    style?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
}> = ({ title, subtitle, children, isLight = false, step, style, contentStyle }) => (<div className="mr-form-section" style={style}>
    <div className="mr-form-section__header">
      {step != null && <div className="mr-form-section__step">{step}</div>}
      <div className="mr-form-section__header-text">
        <div className="mr-form-section__title">{title}</div>
        {subtitle && <div className="mr-form-section__subtitle">— {subtitle}</div>}
      </div>
    </div>
    <div className="mr-form-section__content" style={contentStyle}>
      {children}
    </div>
  </div>);
const MonthDatePicker: React.FC<{
    selected: number[];
    onChange: (dates: number[]) => void;
}> = ({ selected, onChange }) => {
    const { theme } = useTheme();
    const isLight = theme === "light";
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<number[]>(selected);
    const [dropPos, setDropPos] = useState<{
        top: number;
        left: number;
        width: number;
        openAbove: boolean;
    } | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const DROP_HEIGHT = 320;
    const handleOpen = () => {
        setDraft([...selected]);
        if (triggerRef.current) {
            const r = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - r.bottom;
            const openAbove = spaceBelow < DROP_HEIGHT && r.top > spaceBelow;
            setDropPos({
                top: openAbove ? r.top + window.scrollY - DROP_HEIGHT - 6 : r.bottom + window.scrollY + 6,
                left: r.left + window.scrollX,
                width: 300,
                openAbove,
            });
        }
        setOpen(true);
    };
    useEffect(() => {
        const isInside = (t: EventTarget | null) => ref.current?.contains(t as Node) || dropdownRef.current?.contains(t as Node);
        const onMouse = (e: MouseEvent) => {
            if (!isInside(e.target))
                setOpen(false);
        };
        const onScroll = (e: Event) => {
            if (!dropdownRef.current?.contains(e.target as Node))
                setOpen(false);
        };
        document.addEventListener("mousedown", onMouse);
        window.addEventListener("scroll", onScroll, true);
        return () => {
            document.removeEventListener("mousedown", onMouse);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, []);
    const toggle = (d: number) => {
        setDraft((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));
    };
    const handleDone = () => {
        onChange(draft);
        setOpen(false);
    };
    const handleClear = () => setDraft([]);
    return (<div ref={ref} style={{ position: "relative" }}>
      
      <button ref={triggerRef} type="button" onClick={handleOpen} style={{
            width: "100%",
            padding: "10px 14px",
            borderRadius: 10,
            fontSize: 13,
            background: isLight ? "#ffffff" : "rgba(255,255,255,0.05)",
            border: `1px solid ${open ? "rgba(124,58,237,0.5)" : isLight ? "#d1d5db" : "rgba(255,255,255,0.1)"}`,
            color: selected.length > 0 ? (isLight ? "#374151" : "rgba(255,255,255,0.85)") : isLight ? "#9ca3af" : "rgba(255,255,255,0.3)",
            fontFamily: "inherit",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            boxShadow: open ? "0 0 0 3px rgba(124,58,237,0.12)" : "none",
            transition: "all 150ms",
            boxSizing: "border-box",
        }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected.length === 0 ? "Click to select days…" : selected.map((d) => `${d}${ord(d)}`).join(", ")}
        </span>
        <FiCalendar size={14} style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.4)", flexShrink: 0 }}/>
      </button>

      
      {open &&
            dropPos &&
            ReactDOM.createPortal(<AnimatePresence>
            <motion.div ref={dropdownRef} initial={{ opacity: 0, y: dropPos.openAbove ? 6 : -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: dropPos.openAbove ? 6 : -6, scale: 0.98 }} transition={{ duration: 0.15 }} style={{
                    position: "fixed",
                    top: dropPos.top,
                    left: dropPos.left,
                    width: dropPos.width,
                    transform: dropPos.openAbove ? "translateY(-100%)" : "none",
                    zIndex: 9999,
                    maxHeight: `${Math.min(DROP_HEIGHT, dropPos.openAbove ? dropPos.top - window.scrollY - 8 : window.innerHeight - (dropPos.top - window.scrollY) - 8)}px`,
                    overflowY: "auto",
                    background: isLight ? "#ffffff" : "#1e1533",
                    border: isLight ? "1px solid #dbe3ee" : "1px solid rgba(124,58,237,0.35)",
                    borderRadius: 14,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
                    padding: "14px",
                }}>
              
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <span style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600 }}>Select days of month</span>
                <span style={{ color: "#a78bfa", fontSize: 12, fontWeight: 600 }}>
                  {draft.length > 0 ? `${draft.length} selected` : "None selected"}
                </span>
              </div>

              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                    const isSelected = draft.includes(d);
                    return (<button key={d} type="button" onClick={() => toggle(d)} style={{
                            width: "100%",
                            aspectRatio: "1",
                            borderRadius: "50%",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: isSelected ? 700 : 400,
                            fontFamily: "inherit",
                            transition: "all 120ms",
                            background: isSelected ? "linear-gradient(135deg, #7c3aed, #6d28d9)" : isLight ? "#f3f4f6" : "rgba(255,255,255,0.06)",
                            color: isSelected ? "#fff" : isLight ? "#4b5563" : "rgba(255,255,255,0.6)",
                            boxShadow: isSelected ? "0 2px 6px rgba(124,58,237,0.45)" : "none",
                        }} onMouseEnter={(e) => {
                            if (!isSelected)
                                (e.currentTarget as HTMLElement).style.background = "rgba(124,58,237,0.22)";
                        }} onMouseLeave={(e) => {
                            if (!isSelected)
                                (e.currentTarget as HTMLElement).style.background = isLight ? "#f3f4f6" : "rgba(255,255,255,0.06)";
                        }}>
                      {d}
                    </button>);
                })}
              </div>

              
              <div style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.07)",
                }}>
                <button type="button" onClick={handleClear} style={{
                    flex: 1,
                    padding: "8px",
                    borderRadius: 8,
                    border: isLight ? "1px solid #d1d5db" : "1px solid rgba(255,255,255,0.1)",
                    background: "transparent",
                    color: isLight ? "#6b7280" : "rgba(255,255,255,0.45)",
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: "inherit",
                }}>
                  Clear
                </button>
                <button type="button" onClick={handleDone} style={{
                    flex: 2,
                    padding: "8px",
                    borderRadius: 8,
                    border: "none",
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: "0 2px 8px rgba(124,58,237,0.3)",
                }}>
                  Done {draft.length > 0 && `(${draft.length})`}
                </button>
              </div>
            </motion.div>
          </AnimatePresence>, document.body)}
    </div>);
};
const ScheduleSelector: React.FC<{
    form: Omit<Routine, "id" | "createdAt">;
    onChange: (key: string, value: any) => void;
    isLight?: boolean;
}> = ({ form, onChange, isLight = false }) => {
    const TYPES: {
        key: ScheduleType;
        label: string;
        icon: React.ReactNode;
    }[] = [
        { key: "daily", label: "Daily", icon: <FiRefreshCw size={14}/> },
        { key: "weekly", label: "Weekly", icon: <FiCalendar size={14}/> },
        { key: "monthly", label: "Monthly", icon: <FiCalendar size={14}/> },
        { key: "once", label: "Once", icon: <FiClock size={14}/> },
        { key: "interval", label: "Repeat", icon: <FiRefreshCw size={14}/> },
    ];
    const toggleDay = (i: number) => {
        const days = form.scheduleDays.includes(i) ? form.scheduleDays.filter((d) => d !== i) : [...form.scheduleDays, i].sort();
        onChange("scheduleDays", days);
    };
    return (<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      
      <div>
        <FormLabel isLight={isLight}>Schedule Type <span style={{ color: "#ef4444" }}>*</span></FormLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {TYPES.map((t) => {
            const active = form.scheduleType === t.key;
            return (<button key={t.key} type="button" onClick={() => onChange("scheduleType", t.key)} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 18px",
                    borderRadius: 10,
                    border: "1px solid",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: active ? 600 : 400,
                    fontFamily: "inherit",
                    transition: "all 150ms",
                    background: active ? "rgba(124,58,237,0.18)" : isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
                    borderColor: active ? "rgba(124,58,237,0.5)" : isLight ? "#d1d5db" : "rgba(255,255,255,0.1)",
                    color: active ? "#c4b5fd" : isLight ? "#4b5563" : "rgba(255,255,255,0.55)",
                    boxShadow: active ? "0 0 0 3px rgba(124,58,237,0.12)" : "none",
                }}>
                {t.icon} {t.label}
              </button>);
        })}
        </div>
      </div>

      
      {form.scheduleType === "weekly" && (<div>
          <FormLabel isLight={isLight}>
            Days of Week{" "}
            <span style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.25)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              (select one or more)
            </span>
          </FormLabel>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {DAYS_OF_WEEK.map((d, i) => {
                const active = form.scheduleDays.includes(i);
                return (<button key={d} type="button" onClick={() => toggleDay(i)} style={{
                        padding: "8px 16px",
                        borderRadius: 9,
                        border: "1px solid",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                        fontFamily: "inherit",
                        transition: "all 150ms",
                        background: active ? "rgba(124,58,237,0.18)" : isLight ? "#ffffff" : "rgba(255,255,255,0.04)",
                        borderColor: active ? "rgba(124,58,237,0.5)" : isLight ? "#d1d5db" : "rgba(255,255,255,0.1)",
                        color: active ? "#c4b5fd" : isLight ? "#4b5563" : "rgba(255,255,255,0.5)",
                        boxShadow: active ? "0 0 0 2px rgba(124,58,237,0.12)" : "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                    }}>
                  {active && <FiCheck size={11}/>} {d}
                </button>);
            })}
          </div>
          {form.scheduleDays.length === 0 && (<p style={{ color: "#f87171", fontSize: 11, margin: "6px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
              <FiAlertTriangle size={11}/> Select at least one day
            </p>)}
        </div>)}

      
      {form.scheduleType === "monthly" && (<div>
          <FormLabel isLight={isLight}>
            Days of Month{" "}
            <span style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.25)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              (select one or more)
            </span>
          </FormLabel>
          <MonthDatePicker selected={form.scheduleDates} onChange={(dates) => onChange("scheduleDates", dates)}/>
          {form.scheduleDates.length === 0 && (<p style={{ color: "#f87171", fontSize: 11, margin: "6px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
              <FiAlertTriangle size={11}/> Select at least one date
            </p>)}
        </div>)}

      
      {form.scheduleType === "once" && (<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <FormLabel isLight={isLight}>Date</FormLabel>
            <input type="date" value={form.scheduleOnceDate} onChange={(e) => onChange("scheduleOnceDate", e.target.value)} min={new Date().toISOString().split("T")[0]} style={inputStyle(isLight)}/>
          </div>
          <div>
            <FormLabel isLight={isLight}>Time</FormLabel>
            <input type="time" value={form.scheduleTime} onChange={(e) => onChange("scheduleTime", e.target.value)} style={inputStyle(isLight)}/>
          </div>
        </div>)}

      
      {form.scheduleType === "interval" && (<div>
          <FormLabel isLight={isLight}>Repeat every</FormLabel>
          <div role="group" aria-label="Repeat every" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {INTERVAL_OPTIONS.map((opt) => {
                const active = (form.intervalMinutes ?? DEFAULT_INTERVAL_MINUTES) === opt.minutes;
                return (<button key={opt.minutes} type="button" aria-pressed={active} onClick={() => onChange("intervalMinutes", opt.minutes)} style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: active ? "1px solid rgba(124,58,237,0.5)" : isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.1)",
                        background: active ? "rgba(124,58,237,0.15)" : isLight ? "#ffffff" : "rgba(255,255,255,0.03)",
                        color: active ? (isLight ? "#6d28d9" : "#c4b5fd") : "var(--text-secondary)",
                        fontSize: 12.5,
                        fontWeight: active ? 600 : 500,
                        cursor: "pointer",
                        fontFamily: "inherit",
                    }}>
                    {opt.label}
                  </button>);
            })}
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text-tertiary)" }}>
            The first run is about {intervalDuration(form.intervalMinutes)} after saving, then it repeats every {intervalDuration(form.intervalMinutes)}. A run that is still going when the next one is due is skipped.
          </p>
        </div>)}

      {form.scheduleType !== "once" && form.scheduleType !== "interval" && (<div style={{ maxWidth: 200 }}>
          <FormLabel isLight={isLight}>Time</FormLabel>
          <input type="time" value={form.scheduleTime} onChange={(e) => onChange("scheduleTime", e.target.value)} style={inputStyle(isLight)}/>
        </div>)}
    </div>);
};
const RoutineForm: React.FC<{
    initial?: Routine | null;
    initialName?: string;
    initialPrefill?: Partial<Omit<Routine, "id" | "createdAt">>;
    dwAgents?: DwAgentRecord[];
    onSave: (data: Omit<Routine, "id" | "createdAt">) => void;
    onCancel: () => void;
    onPreview?: () => void;
    saving?: boolean;
    generating?: boolean;
}> = ({ initial, initialName, initialPrefill, dwAgents = [], onSave, onCancel, onPreview, saving = false, generating = false }) => {
    const { theme } = useTheme();
    const isLight = theme === "light";
    const [promptCopied, setPromptCopied] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [previewMermaidSvg, setPreviewMermaidSvg] = useState("");
    const [previewEmailHtml, setPreviewEmailHtml] = useState("");
    const [previewRawFallback, setPreviewRawFallback] = useState("");
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const scrollPromptToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            const el = promptRef.current;
            if (el)
                el.scrollTop = el.scrollHeight;
        });
    }, []);
    const streamTargetRef = useRef("");
    const streamShownRef = useRef(0);
    const streamRafRef = useRef<number | null>(null);
    const revealStreamTick = useCallback(() => {
        const target = streamTargetRef.current;
        const gap = target.length - streamShownRef.current;
        if (gap > 0) {
            streamShownRef.current += Math.min(18, Math.max(4, Math.ceil(gap * 0.3)));
            set("prompt", target.slice(0, streamShownRef.current));
            scrollPromptToBottom();
        }
        streamRafRef.current = requestAnimationFrame(revealStreamTick);
    }, [scrollPromptToBottom]);
    const startStreamReveal = useCallback(() => {
        streamTargetRef.current = "";
        streamShownRef.current = 0;
        if (streamRafRef.current == null) {
            streamRafRef.current = requestAnimationFrame(revealStreamTick);
        }
    }, [revealStreamTick]);
    const stopStreamReveal = useCallback((snapToFinal: boolean) => {
        if (streamRafRef.current != null) {
            cancelAnimationFrame(streamRafRef.current);
            streamRafRef.current = null;
        }
        if (snapToFinal && streamTargetRef.current) {
            set("prompt", streamTargetRef.current);
        }
    }, []);
    const pushStreamChunk = useCallback((value: string) => {
        streamTargetRef.current = value;
    }, []);
    const insertPromptMd = useCallback((before: string, after: string, linePrefix = "") => {
        const el = promptRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix ? `${linePrefix}${selected || "text"}` : `${before}${selected || "text"}${after}`;
        set("prompt", value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const np = s + replacement.length;
            el.setSelectionRange(np, np);
            el.focus();
        }, 0);
    }, []);
    const [form, setForm] = useState<Omit<Routine, "id" | "createdAt">>(() => {
        const base = initial ? { ...initial } : { ...defaultRoutine(), ...(initialPrefill || {}), name: initialName || "" };
        return { ...base, scheduleType: normalizeScheduleType(base.scheduleType) };
    });
    const [submitted, setSubmitted] = useState(false);
    const userChangedAgentRef = useRef(false);
    useEffect(() => {
        if (initial || userChangedAgentRef.current || form.assigneeAgentId || dwAgents.length === 0)
            return;
        const primary = dwAgents.find((a) => a.is_primary_agent) || dwAgents[0];
        set("assigneeAgentId", primary.agent_id);
        set("assigneeAgentName", primary.name);
    }, [initial, dwAgents, form.assigneeAgentId]);
    useEffect(() => {
        if (!generating || !promptRef.current)
            return;
        promptRef.current.scrollTop = promptRef.current.scrollHeight;
    }, [form.prompt, generating]);
    const isEdit = !!initial;
    const set = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));
    useEffect(() => {
        if (initial)
            return;
        setForm((prev) => ({
            ...prev,
            ...(initialPrefill || {}),
            ...(typeof initialName === "string" ? { name: initialName } : {}),
        }));
    }, [initial, initialName, initialPrefill]);
    const handleCopyPrompt = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(form.prompt || "");
            setPromptCopied(true);
            setTimeout(() => setPromptCopied(false), 1500);
        }
        catch {
        }
    }, [form.prompt]);
    const [aiEditInput, setAiEditInput] = useState("");
    const [aiEditLoading, setAiEditLoading] = useState(false);
    const [aiEditError, setAiEditError] = useState("");
    const handleAiEditPrompt = useCallback(async () => {
        const instruction = aiEditInput.trim();
        if (!instruction || aiEditLoading)
            return;
        if (!form.prompt.trim()) {
            setAiEditError("Write a prompt above first — AI needs existing instructions to update.");
            return;
        }
        setAiEditLoading(true);
        setAiEditError("");
        try {
            const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/routine-builder`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify({ existing_instruction: form.prompt, new_requirement: instruction }),
            });
            if (!res.ok)
                throw new Error("Routine builder request failed");
            startStreamReveal();
            const completed = await streamRoutineInstruction(res, pushStreamChunk);
            stopStreamReveal(true);
            if (!completed)
                throw new Error("Routine builder returned no updated prompt");
            setAiEditInput("");
        }
        catch {
            setAiEditError("Couldn't update the prompt — please try again.");
        }
        finally {
            stopStreamReveal(false);
            setAiEditLoading(false);
        }
    }, [aiEditInput, aiEditLoading, form.prompt, startStreamReveal, stopStreamReveal, pushStreamChunk]);
    const handlePreviewPrompt = useCallback(async () => {
        if (!form.prompt.trim() || previewLoading)
            return;
        setPreviewModalOpen(true);
        setPreviewLoading(true);
        setPreviewError("");
        setPreviewMermaidSvg("");
        setPreviewEmailHtml("");
        setPreviewRawFallback("");
        try {
            const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/routine-preview`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify({ prompt: form.prompt }),
            });
            if (!res.ok)
                throw new Error("Routine preview request failed");
            let finalText = "";
            const got = await streamRoutinePreview(res, (v) => {
                finalText = v;
            });
            if (!got)
                throw new Error("Routine preview returned no output");
            const mermaidBlock = extractRoutineMarker(finalText, "MERMAID_DIAGRAM");
            const emailBlock = extractRoutineMarker(finalText, "SAMPLE_EMAIL");
            if (mermaidBlock?.complete && mermaidBlock.value) {
                try {
                    const { svg } = await mermaid.render("mr-preview-mermaid", mermaidBlock.value);
                    setPreviewMermaidSvg(svg);
                }
                catch {
                }
            }
            if (emailBlock?.complete && emailBlock.value) {
                setPreviewEmailHtml(emailBlock.value);
            }
            if (!mermaidBlock && !emailBlock) {
                setPreviewRawFallback(finalText);
            }
        }
        catch {
            setPreviewError("Couldn't generate a preview — please try again.");
        }
        finally {
            setPreviewLoading(false);
        }
    }, [form.prompt, previewLoading]);
    const previewStatusNode = (emptyLabel?: string) => {
        if (previewLoading) {
            return (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: isLight ? "#6b7280" : "rgba(255,255,255,0.4)" }}>
          <FiRefreshCw size={20} style={{ animation: "spin 1s linear infinite" }}/>
          <span style={{ fontSize: 13 }}>Generating preview…</span>
        </div>);
        }
        if (previewError) {
            return (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#f87171" }}>
          <FiAlertTriangle size={20}/>
          <span style={{ fontSize: 13 }}>{previewError}</span>
        </div>);
        }
        return <span style={{ fontSize: 13, color: isLight ? "#9ca3af" : "rgba(255,255,255,0.3)" }}>{emptyLabel}</span>;
    };
    const [aiFileLoading, setAiFileLoading] = useState(false);
    const [aiFileError, setAiFileError] = useState("");
    const aiFileInputRef = useRef<HTMLInputElement>(null);
    const handleAiFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || aiFileLoading)
            return;
        setAiFileLoading(true);
        setAiFileError("");
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("prompt", aiEditInput.trim());
            const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/routine-builder/from-file`, {
                method: "POST",
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken") || ""}` },
                body: formData,
            });
            if (!res.ok) {
                let detail = "";
                try {
                    const errJson = await res.json();
                    if (typeof errJson?.errorMessage === "string")
                        detail = errJson.errorMessage;
                }
                catch {
                }
                setAiFileError(detail || "Couldn't generate a prompt from that file — please try again.");
                return;
            }
            startStreamReveal();
            const completed = await streamRoutineInstruction(res, pushStreamChunk);
            stopStreamReveal(true);
            if (!completed)
                throw new Error("Routine builder returned no prompt from file");
            setAiEditInput("");
        }
        catch {
            setAiFileError("Couldn't generate a prompt from that file — please try again.");
        }
        finally {
            stopStreamReveal(false);
            setAiFileLoading(false);
        }
    }, [aiEditInput, aiFileLoading, startStreamReveal, stopStreamReveal, pushStreamChunk]);
    const [scopeChosen, setScopeChosen] = useState<boolean>(!!initial);
    const selectedScope: "all" | "reports" | "" = !scopeChosen
        ? ""
        : form.skillNames.some((n: string) => n.toLowerCase().includes("report"))
            ? "reports"
            : "all";
    const handleScopeChange = (scope: "all" | "reports") => {
        setScopeChosen(true);
        if (scope === "all") {
            set("skillIds", []);
            set("skillNames", []);
        }
        else {
            set("skillIds", ["reports"]);
            set("skillNames", ["Reports"]);
        }
    };
    // Agent is mandatory. A routine loaded or prefilled with an agent id but no
    // name (template, older routine) takes the name from the agent list, so it
    // is never saved as null.
    const selectedAgentName = (form.assigneeAgentName?.trim()
        || dwAgents.find((a) => a.agent_id === form.assigneeAgentId)?.name?.trim()
        || "");
    const agentMissing = !form.assigneeAgentId || !selectedAgentName;
    const isValid = () => {
        if (!form.name.trim())
            return false;
        if (agentMissing)
            return false;
        if (!SCHEDULE_TYPES.includes(form.scheduleType))
            return false;
        if (form.scheduleType === "weekly" && form.scheduleDays.length === 0)
            return false;
        if (form.scheduleType === "monthly" && form.scheduleDates.length === 0)
            return false;
        if (form.scheduleType === "interval" && !((form.intervalMinutes ?? 0) >= 5))
            return false;
        return true;
    };
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        if (!isValid())
            return;
        onSave({ ...form, assigneeAgentName: selectedAgentName });
    };
    return (<motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }}>
      
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onCancel} style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: isLight ? "#6b7280" : "rgba(255,255,255,0.4)",
            fontSize: 12,
            fontFamily: "inherit",
            padding: 0,
        }}>
            <FiChevronLeft size={14}/> Back
          </button>
          <div style={{ width: 1, height: 14, background: isLight ? "#d1d5db" : "rgba(255,255,255,0.1)" }}/>
          <h1 style={{ color: isLight ? "#111827" : "rgba(255,255,255,0.92)", fontSize: 16, fontWeight: 700, margin: 0, lineHeight: 1.15 }}>
            {isEdit ? "Edit Routine" : "Create New Routine"}
          </h1>
        </div>
        {isEdit && onPreview && (<button onClick={onPreview} style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: isLight ? "1px solid #d1d5db" : "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                cursor: "pointer",
                color: isLight ? "#374151" : "rgba(255,255,255,0.75)",
                fontSize: 12,
                fontWeight: 500,
                fontFamily: "inherit",
                padding: "4px 10px",
            }}>
            <FiEye size={12}/> Details
          </button>)}
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.45fr", gap: 14, alignItems: "stretch", width: "100%", opacity: generating ? 0.35 : 1, pointerEvents: generating ? "none" : "auto" }}>
          
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            
            <FormSection title="Basic Information" subtitle="Name and describe your routine" isLight={isLight} step={1}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <FormLabel isLight={isLight}>Routine Name *</FormLabel>
                  <input type="text" placeholder="e.g. Daily Commission Check" value={form.name} onChange={(e) => set("name", e.target.value)} style={{
            ...inputStyle(isLight),
            borderColor: submitted && !form.name.trim() ? "#ef4444" : isLight ? "#d1d5db" : "rgba(255,255,255,0.1)",
        }}/>
                  {submitted && !form.name.trim() && (<p style={{ color: "#f87171", fontSize: 11, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
                      <FiAlertTriangle size={11}/> Routine name is required
                    </p>)}
                </div>
                <div>
                  <FormLabel isLight={isLight}>Description</FormLabel>
                  <textarea placeholder="What does this routine do?" value={form.description} onChange={(e) => set("description", e.target.value)} rows={5} style={{ ...inputStyle(isLight), resize: "vertical" }}/>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  
                  <div style={{ flex: 1 }}>
                    <FormLabel isLight={isLight}>Agent Name *</FormLabel>
                    <select value={form.assigneeAgentId || ""} onChange={(e) => {
            userChangedAgentRef.current = true;
            const agentId = e.target.value;
            const agent = dwAgents.find((a) => a.agent_id === agentId);
            set("assigneeAgentId", agent ? agentId : undefined);
            set("assigneeAgentName", agent?.name ?? undefined);
        }} style={{
            ...inputStyle(isLight),
            borderColor: submitted && agentMissing ? "#ef4444" : isLight ? "#d1d5db" : "rgba(255,255,255,0.1)",
            background: isLight ? "#ffffff" : "transparent",
            color: isLight ? "#111827" : "#e2e8f0",
            colorScheme: isLight ? "light" : "dark",
            appearance: "none",
            WebkitAppearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${isLight ? "%236b7280" : "%23a0aec0"}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 12px center",
            paddingRight: 36,
            cursor: "pointer",
        }}>
                      {!form.assigneeAgentId && (<option value="" disabled style={{ background: isLight ? "#ffffff" : "#1e1635", color: isLight ? "#6b7280" : "#a0aec0" }}>
                          Select an agent
                        </option>)}
                      {form.assigneeAgentId && !dwAgents.some((a) => a.agent_id === form.assigneeAgentId) && (<option value={form.assigneeAgentId} style={{ background: isLight ? "#ffffff" : "#1e1635", color: isLight ? "#111827" : "#e2e8f0" }}>
                          {form.assigneeAgentName || form.assigneeAgentId}
                        </option>)}
                      {[...dwAgents]
            .sort((a, b) => (b.is_primary_agent ? 1 : 0) - (a.is_primary_agent ? 1 : 0))
            .map((agent) => (<option key={agent.agent_id} value={agent.agent_id} style={{ background: isLight ? "#ffffff" : "#1e1635", color: isLight ? "#111827" : "#e2e8f0" }}>
                            {agent.is_primary_agent ? `★ ${agent.name} (Primary)` : agent.name}
                          </option>))}
                    </select>
                    {submitted && agentMissing && (<p style={{ color: "#f87171", fontSize: 11, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
                        <FiAlertTriangle size={11}/> {form.assigneeAgentId ? "This agent is no longer available — choose another" : "Agent is required"}
                      </p>)}
                  </div>
                </div>
              </div>
            </FormSection>

            
            <FormSection title="Schedule" subtitle="When and how often this routine runs" isLight={isLight} step={2}>
              <ScheduleSelector form={form} onChange={set} isLight={isLight}/>
            </FormSection>

            
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 2px 8px" }}>
              <ToggleSwitch on={form.isActive} onChange={() => set("isActive", !form.isActive)}/>
              <div>
                <div style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.75)", fontSize: 13, fontWeight: 500 }}>
                  {form.isActive ? "Active" : "Paused"}
                </div>
                <div style={{ color: isLight ? "#9ca3af" : "rgba(255,255,255,0.3)", fontSize: 11 }}>
                  {form.isActive ? "Runs on schedule" : "Will not run"}
                </div>
              </div>
            </div>
          </div>

          
          <div style={{ display: "flex", flexDirection: "column", gap: 10, height: "100%" }}>
            <FormSection title="Prompt" subtitle="What the AI does each time this routine runs" isLight={isLight} step={3} style={{ flex: 1, display: "flex", flexDirection: "column" }} contentStyle={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <div className="ca-fp-editor__body" style={{ padding: "10px 0 4px", overflow: "visible", flex: 1, display: "flex", flexDirection: "column" }}>
                
                <div className="ca-fp-editor__toolbar-row">
                  <div className="ca-fp-md-toolbar">
                    <button type="button" onClick={() => insertPromptMd("**", "**")}>
                      <b>B</b>
                    </button>
                    <button type="button" onClick={() => insertPromptMd("*", "*")}>
                      <em>I</em>
                    </button>
                    <span className="ca-fp-md-toolbar__div"/>
                    <button type="button" onClick={() => insertPromptMd("", "", "# ")}>
                      H1
                    </button>
                    <button type="button" onClick={() => insertPromptMd("", "", "## ")}>
                      H2
                    </button>
                    <button type="button" onClick={() => insertPromptMd("", "", "### ")}>
                      H3
                    </button>
                    <span className="ca-fp-md-toolbar__div"/>
                    <button type="button" onClick={() => insertPromptMd("", "", "- ")}>
                      UL
                    </button>
                    <button type="button" onClick={() => insertPromptMd("", "", "1. ")}>
                      OL
                    </button>
                    <span className="ca-fp-md-toolbar__div"/>
                    <button type="button" onClick={() => insertPromptMd("`", "`")}>
                      {"`  `"}
                    </button>
                    <button type="button" onClick={() => insertPromptMd("", "", "> ")}>
                      ❝
                    </button>
                  </div>
                  <span className="ca-fp-editor__charcount">{form.prompt.length.toLocaleString()} chars</span>
                  <button type="button" onClick={handlePreviewPrompt} disabled={!form.prompt.trim() || previewLoading} title="Preview" style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 9px",
            borderRadius: 6,
            border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.1)",
            background: "transparent",
            color: isLight ? "#6b7280" : "rgba(255,255,255,0.5)",
            fontSize: 11,
            fontWeight: 500,
            cursor: form.prompt.trim() && !previewLoading ? "pointer" : "not-allowed",
            opacity: form.prompt.trim() && !previewLoading ? 1 : 0.5,
            fontFamily: "inherit",
        }}>
                    <FiEye size={11}/> Preview
                  </button>
                  <button type="button" onClick={handleCopyPrompt} disabled={!form.prompt} title="Copy prompt" style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 9px",
            borderRadius: 6,
            border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.1)",
            background: "transparent",
            color: isLight ? "#6b7280" : "rgba(255,255,255,0.5)",
            fontSize: 11,
            fontWeight: 500,
            cursor: form.prompt ? "pointer" : "not-allowed",
            opacity: form.prompt ? 1 : 0.5,
            fontFamily: "inherit",
        }}>
                    {promptCopied ? (<>
                        <FiCheck size={11}/> Copied
                      </>) : (<>
                        <FiCopy size={11}/> Copy
                      </>)}
                  </button>
                </div>

                <textarea ref={promptRef} className="ca-fp-editor__textarea" placeholder="Write the instructions for the AI to follow when this routine executes…" value={form.prompt} onChange={(e) => set("prompt", e.target.value)} spellCheck={false} style={{ flex: 1, minHeight: 220, maxHeight: "56vh", resize: "none", overflowY: "auto" }}/>
                <p style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.25)", fontSize: 11, margin: "6px 0 0" }}>
                  Be specific — the AI will use this prompt verbatim each time the routine runs.
                </p>

                
                <div className="cc-ai-task-row" style={{ marginTop: 10 }}>
                  <div className="cc-ai-task-input-bar">
                    <input className="cc-ai-input" placeholder="Ask AI to update this prompt — e.g. “make it shorter”…" value={aiEditInput} onChange={(e) => setAiEditInput(e.target.value)} onKeyDown={(e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                handleAiEditPrompt();
            }
        }} disabled={aiEditLoading}/>
                    <button type="button" className="cc-ai-assign-btn" onClick={handleAiEditPrompt} disabled={!aiEditInput.trim() || aiEditLoading}>
                      {aiEditLoading ? (<>
                          <FiRefreshCw size={13} style={{ animation: "spin 0.8s linear infinite" }}/>
                          Updating...
                        </>) : (<>
                          <FiZap size={13}/> Send
                        </>)}
                    </button>
                  </div>
                </div>
                {aiEditError && (<p style={{ color: "#f87171", fontSize: 11, margin: "6px 0 0" }}>{aiEditError}</p>)}

                
                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0" }}>
                  <div style={{ flex: 1, height: 1, background: isLight ? "#e5e7eb" : "rgba(255,255,255,0.08)" }}/>
                  <span style={{ fontSize: 11, fontWeight: 600, color: isLight ? "#9ca3af" : "rgba(255,255,255,0.3)" }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: isLight ? "#e5e7eb" : "rgba(255,255,255,0.08)" }}/>
                </div>

                <input ref={aiFileInputRef} type="file" accept=".pdf,.md" style={{ display: "none" }} onChange={handleAiFileSelected}/>
                <button type="button" onClick={() => aiFileInputRef.current?.click()} disabled={aiFileLoading} style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: isLight ? "1px dashed #d1d5db" : "1px dashed rgba(255,255,255,0.15)",
            background: "transparent",
            color: isLight ? "#4b5563" : "rgba(255,255,255,0.5)",
            fontSize: 12.5,
            fontWeight: 500,
            cursor: aiFileLoading ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            opacity: aiFileLoading ? 0.6 : 1,
        }}>
                  {aiFileLoading ? (<>
                      <FiRefreshCw size={13} style={{ animation: "spin 0.8s linear infinite" }}/>
                      Generating prompt from file…
                    </>) : (<>
                      <FiPaperclip size={13}/> Upload PDF or Markdown to generate prompt
                    </>)}
                </button>
                {aiFileError && (<p style={{ color: "#f87171", fontSize: 11, margin: "6px 0 0" }}>{aiFileError}</p>)}
              </div>
            </FormSection>

            
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingBottom: 28 }}>
              <button type="button" onClick={onCancel} style={{
            padding: "9px 22px",
            borderRadius: 10,
            border: isLight ? "1px solid #d1d5db" : "1px solid rgba(255,255,255,0.12)",
            background: "transparent",
            color: isLight ? "#4b5563" : "rgba(255,255,255,0.6)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
        }}>
                Cancel
              </button>
              <button type="submit" disabled={saving || generating} style={{
            padding: "9px 26px",
            borderRadius: 10,
            border: "none",
            background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: saving || generating ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
            opacity: saving || generating ? 0.75 : 1,
            display: "flex",
            alignItems: "center",
            gap: 7,
        }}>
                {(saving || generating) && <FiRefreshCw size={13} style={{ animation: "spin 0.8s linear infinite" }}/>}
                {generating ? "Creating..." : isEdit ? "Save Changes" : "Create Routine"}
              </button>
            </div>
          </div>
          
          </div>
        </div>
      </form>

      {ReactDOM.createPortal(<AnimatePresence>
          {previewModalOpen && (<motion.div key="preview-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={() => setPreviewModalOpen(false)} style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 100000,
                    background: "rgba(8,5,20,0.72)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 28,
                }}>
              <motion.div key="preview-dialog" initial={{ opacity: 0, scale: 0.96, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97, y: 8 }} transition={{ duration: 0.2, ease: "easeOut" }} onClick={(e) => e.stopPropagation()} style={{
                    width: "100%",
                    maxWidth: 1360,
                    height: "88vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 20,
                    overflow: "hidden",
                    background: isLight ? "#ffffff" : "#150e28",
                    border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                }}>
                
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 24px",
                    flexShrink: 0,
                    borderBottom: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
                }}>
                      <FiEye size={16} color="#fff"/>
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: isLight ? "#111827" : "rgba(255,255,255,0.94)" }}>
                        Routine Preview
                      </h2>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: isLight ? "#6b7280" : "rgba(255,255,255,0.4)" }}>
                        Workflow diagram and sample output generated from this prompt
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setPreviewModalOpen(false)} title="Close" style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    border: "none",
                    flexShrink: 0,
                    background: isLight ? "#f3f4f6" : "rgba(255,255,255,0.06)",
                    color: isLight ? "#4b5563" : "rgba(255,255,255,0.65)",
                    cursor: "pointer",
                }}>
                    <FiX size={17}/>
                  </button>
                </div>

                
                <div style={{
                    flex: 1,
                    display: "flex",
                    gap: 18,
                    padding: 20,
                    overflow: "hidden",
                    background: isLight ? "#f9fafb" : "#0c0c0c",
                }}>
                  
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: isLight ? "#ffffff" : "#150e28",
                    border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: isLight ? "0 2px 10px rgba(15,23,42,0.05)" : "none",
                }}>
                    <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 16px",
                    flexShrink: 0,
                    borderBottom: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.07)",
                }}>
                      <FiGitBranch size={13} style={{ color: "#7c3aed" }}/>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: isLight ? "#374151" : "rgba(255,255,255,0.78)" }}>
                        Workflow Diagram
                      </span>
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", padding: 22 }}>
                      {previewLoading || previewError ? (<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>{previewStatusNode()}</div>) : previewMermaidSvg ? (<div dangerouslySetInnerHTML={{ __html: previewMermaidSvg }}/>) : (<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {previewStatusNode("No diagram available.")}
                        </div>)}
                    </div>
                  </div>

                  
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: isLight ? "#ffffff" : "#150e28",
                    border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: isLight ? "0 2px 10px rgba(15,23,42,0.05)" : "none",
                }}>
                    <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 16px",
                    flexShrink: 0,
                    borderBottom: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.07)",
                }}>
                      <FiMail size={13} style={{ color: "#7c3aed" }}/>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: isLight ? "#374151" : "rgba(255,255,255,0.78)" }}>
                        Sample Email
                      </span>
                    </div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                      {previewLoading || previewError ? (<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>{previewStatusNode()}</div>) : previewEmailHtml ? (<iframe title="Routine preview email" srcDoc={previewEmailHtml} sandbox="" style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}/>) : previewRawFallback ? (<pre style={{
                        margin: 0,
                        flex: 1,
                        overflow: "auto",
                        padding: 22,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "inherit",
                        fontSize: 13,
                        color: isLight ? "#111827" : "rgba(255,255,255,0.85)",
                    }}>
                          {previewRawFallback}
                        </pre>) : (<div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {previewStatusNode("No email preview available.")}
                        </div>)}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>)}
        </AnimatePresence>, document.body)}
    </motion.div>);
};
function routineHeaders() {
    return { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken") || ""}` };
}
const ROUTINE_SUGGESTIONS = [
    { text: "Send renewal reminder to clients expiring in the next 30 days", tag: "Renewals" },
    { text: "Compile weekly production report for all active agents", tag: "Reports" },
    { text: "Review and flag all open service requests older than 72 hours", tag: "Service" },
];
function mapRoutineTrigger(trigger?: string): ScheduleType {
    const t = (trigger || "").toLowerCase();
    if (t.includes("week"))
        return "weekly";
    if (t.includes("month"))
        return "monthly";
    if (t.includes("once") || t.includes("one-time") || t.includes("one time"))
        return "once";
    return "daily";
}
function extractRoutineMarker(text: string, marker: string): {
    value: string;
    complete: boolean;
} | null {
    const startTag = `$$${marker}_START$$`;
    const endTag = `$$${marker}_END$$`;
    const start = text.indexOf(startTag);
    if (start === -1)
        return null;
    const contentStart = start + startTag.length;
    const end = text.indexOf(endTag, contentStart);
    if (end !== -1)
        return { value: text.slice(contentStart, end).trim(), complete: true };
    const partial = text.slice(contentStart).replace(/(?:\$\$[A-Z_]*\$?|\$)$/, "");
    return { value: partial, complete: false };
}
async function streamRoutineInstruction(response: Response, onUpdate: (value: string) => void): Promise<boolean> {
    if (!response.body)
        return false;
    let streamText = "";
    let done = false;
    const applyMarker = () => {
        if (done)
            return;
        const m = extractRoutineMarker(streamText, "ROUTINE_INSTRUCTION");
        if (m) {
            done = m.complete;
            onUpdate(m.value);
        }
    };
    const parseDataLine = (chunk: string) => {
        const dataLine = chunk
            .split(/\r?\n/)
            .map((l) => l.trim())
            .find((l) => l.startsWith("data:"));
        if (!dataLine)
            return;
        let json: any = null;
        try {
            json = JSON.parse(dataLine.replace(/^data:\s*/, ""));
        }
        catch {
            return;
        }
        if (json?.errorMessage)
            throw new Error(json.errorMessage);
        if (typeof json?.response === "string") {
            streamText += json.response;
            applyMarker();
        }
    };
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    while (true) {
        const { done: readerDone, value } = await reader.read();
        if (readerDone)
            break;
        sseBuffer += decoder.decode(value, { stream: true });
        const events = sseBuffer.split("\n\n");
        sseBuffer = events.pop() ?? "";
        for (const event of events)
            parseDataLine(event);
    }
    if (sseBuffer.trim()) {
        try {
            parseDataLine(sseBuffer);
        }
        catch {
        }
    }
    return done;
}
async function streamRoutinePreview(response: Response, onUpdate: (value: string) => void): Promise<boolean> {
    if (!response.body)
        return false;
    let streamText = "";
    const parseDataLine = (chunk: string) => {
        const dataLine = chunk
            .split(/\r?\n/)
            .map((l) => l.trim())
            .find((l) => l.startsWith("data:"));
        if (!dataLine)
            return;
        let json: any = null;
        try {
            json = JSON.parse(dataLine.replace(/^data:\s*/, ""));
        }
        catch {
            return;
        }
        if (json?.errorMessage)
            throw new Error(json.errorMessage);
        if (typeof json?.response === "string") {
            streamText += json.response;
            onUpdate(streamText);
        }
    };
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    while (true) {
        const { done: readerDone, value } = await reader.read();
        if (readerDone)
            break;
        sseBuffer += decoder.decode(value, { stream: true });
        const events = sseBuffer.split("\n\n");
        sseBuffer = events.pop() ?? "";
        for (const event of events)
            parseDataLine(event);
    }
    if (sseBuffer.trim()) {
        try {
            parseDataLine(sseBuffer);
        }
        catch {
        }
    }
    return streamText.length > 0;
}
mermaid.initialize({ startOnLoad: false, securityLevel: "strict" });
function mapApiRoutine(r: any): Routine {
    return {
        id: String(r.id),
        name: r.name ?? "",
        description: r.description ?? "",
        prompt: r.prompt ?? "",
        skillIds: Array.isArray(r.skillIds) ? r.skillIds : [],
        skillNames: Array.isArray(r.skillNames) ? r.skillNames : [],
        assigneeAgentId: r.assigneeAgentId ?? undefined,
        assigneeAgentName: r.assigneeAgentName ?? undefined,
        scheduleType: normalizeScheduleType(r.scheduleType),
        scheduleTime: r.scheduleTime ?? "09:00",
        scheduleTimeTz: r.scheduleTimeTz ?? "UTC",
        scheduleDays: Array.isArray(r.scheduleDays) ? r.scheduleDays : [],
        scheduleDates: Array.isArray(r.scheduleDates) ? r.scheduleDates : [],
        scheduleOnceDate: r.scheduleOnceDate ?? "",
        intervalMinutes: r.intervalMinutes ?? r.interval_minutes ?? undefined,
        isActive: r.isActive ?? true,
        isSystem: r.isSystem ?? false,
        lastRunAt: r.lastRunAt ?? undefined,
        nextRunAt: r.nextRunAt ?? undefined,
        createdAt: r.createdAt ?? new Date().toISOString(),
    };
}
function mapApiRoutineTemplate(t: any): RoutineTemplate {
    return {
        id: String(t.id),
        name: t.name ?? "",
        description: t.description ?? "",
        prompt: t.prompt ?? "",
        skillIds: Array.isArray(t.skillIds) ? t.skillIds : [],
        skillNames: Array.isArray(t.skillNames) ? t.skillNames : [],
        assigneeAgentId: t.assigneeAgentId ?? undefined,
        assigneeAgentName: t.assigneeAgentName ?? undefined,
        scheduleType: normalizeScheduleType(t.scheduleType),
        scheduleTime: t.scheduleTime ?? "09:00",
        scheduleTimeTz: t.scheduleTimeTz ?? "UTC",
        scheduleDays: Array.isArray(t.scheduleDays) ? t.scheduleDays : [],
        scheduleDates: Array.isArray(t.scheduleDates) ? t.scheduleDates : [],
        scheduleOnceDate: t.scheduleOnceDate ?? "",
        category: t.category ?? undefined,
        icon: t.icon ?? undefined,
    };
}
const MomentumRoutinesPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme } = useTheme();
    const isLight = theme === "light";
    const { setStats: setRoutinesStats } = useRoutinesStats();
    const [routines, setRoutines] = useState<Routine[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<"list" | "create" | "edit" | "detail">("list");
    const [editingRoutine, setEditingRoutine] = useState<Routine | null>(null);
    const [viewingRoutine, setViewingRoutine] = useState<Routine | null>(null);
    const [deepLinkRunId, setDeepLinkRunId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [toast, setToast] = useState<{
        type: "success" | "error";
        msg: string;
    } | null>(null);
    const [saving, setSaving] = useState(false);
    const [routineInput, setRoutineInput] = useState("");
    const [routineSuggestionsOpen, setRoutineSuggestionsOpen] = useState(false);
    const [prefilledName, setPrefilledName] = useState("");
    const [routineBuilderPrefill, setRoutineBuilderPrefill] = useState<Partial<Omit<Routine, "id" | "createdAt">> | null>(null);
    const [buildingRoutine, setBuildingRoutine] = useState(false);
    const [dwAgents, setDwAgents] = useState<DwAgentRecord[]>([]);
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [templates, setTemplates] = useState<RoutineTemplate[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [search, setSearch] = useState("");
    // All / Active / Paused — applied to the routines loaded so far (the list API
    // only filters by search text). A filter that hides most rows keeps the
    // load-more sentinel on screen, so further pages keep loading.
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused">("all");
    const visibleRoutines = useMemo(() => (statusFilter === "all"
        ? routines
        : routines.filter((r) => (statusFilter === "active" ? r.isActive : !r.isActive))), [routines, statusFilter]);
    const searchRef = useRef("");
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3000);
    };
    // Agents only feed the create/edit form (RoutineForm) — load them the first
    // time it opens, not with the list. The form's default-agent pick waits for them.
    const dwAgentsRequestedRef = useRef(false);
    useEffect(() => {
        if (view !== "create" && view !== "edit")
            return;
        if (dwAgentsRequestedRef.current)
            return;
        dwAgentsRequestedRef.current = true;
        listDwAgents(false, true)
            .then((records) => setDwAgents(records.filter((r) => !r.is_archived)))
            .catch(() => { dwAgentsRequestedRef.current = false; setDwAgents([]); });
    }, [view]);
    // Legacy ?routineId=<id>[&runId=] links (old bookmarks/messages): send them to
    // the normal /momentum-routines/<id> route, which loads the routine itself.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const routineId = params.get("routineId");
        if (!routineId)
            return;
        const runId = params.get("runId");
        navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(routineId)}${runId ? `?runId=${encodeURIComponent(runId)}` : ""}`, { replace: true });
    }, [location.search]);
    const routinePathId = location.pathname.startsWith(`${ROUTES.MOMENTUM_ROUTINES}/`)
        ? decodeURIComponent(location.pathname.slice(ROUTES.MOMENTUM_ROUTINES.length + 1).split("/")[0] || "").trim()
        : "";
    const goToRoutineDetail = useCallback((id: string) => {
        navigate(`${ROUTES.MOMENTUM_ROUTINES}/${encodeURIComponent(id)}`);
    }, [navigate]);
    const goToRoutinesList = useCallback(() => {
        navigate(ROUTES.MOMENTUM_ROUTINES);
    }, [navigate]);
    useEffect(() => {
        if (!routinePathId || view === "edit" || view === "create")
            return;
        if (viewingRoutine && String(viewingRoutine.id) === routinePathId)
            return;
        let cancelled = false;
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${routinePathId}`, { headers: routineHeaders() })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            if (cancelled || !json?.data)
                return;
            setViewingRoutine(mapApiRoutine(json.data));
            setView("detail");
        })
            .catch(() => {
            if (cancelled)
                return;
            showToast("error", "Could not find that routine.");
            navigate(ROUTES.MOMENTUM_ROUTINES, { replace: true });
        });
        return () => {
            cancelled = true;
        };
    }, [routinePathId, view]);
    useEffect(() => {
        const runId = new URLSearchParams(location.search).get("runId");
        if (runId)
            setDeepLinkRunId(runId);
    }, [location.search]);
    useEffect(() => {
        if (!routinePathId && view === "detail") {
            setView("list");
            setViewingRoutine(null);
            setDeepLinkRunId(null);
        }
    }, [routinePathId]);
    // Opened straight onto one routine (/momentum-routines/<id>): the list is hidden
    // behind the detail view, so it waits until the user goes back to the list.
    const listDeferredRef = useRef<boolean>(!!routinePathId);
    const fetchRoutines = useCallback((pageNum: number = 1, silent: boolean = false) => {
        listDeferredRef.current = false;
        if (!silent) {
            if (pageNum === 1)
                setLoading(true);
            else
                setLoadingMore(true);
        }
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/list?page=${pageNum}&pageSize=10`, {
            method: "POST",
            headers: routineHeaders(),
            body: JSON.stringify({ searchBy: searchRef.current.trim() || null }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            const total: number = json?.totalCount ?? list.length;
            const mapped = list.map(mapApiRoutine);
            setRoutines((prev) => (pageNum === 1 ? mapped : [...prev, ...mapped]));
            setHasMore(pageNum * 10 < total);
        })
            .catch(() => {
            if (pageNum === 1 && !silent)
                setRoutines([]);
        })
            .finally(() => {
            if (!silent) {
                if (pageNum === 1)
                    setLoading(false);
                else
                    setLoadingMore(false);
            }
        });
    }, []);
    const handleOpenLibrary = useCallback(() => {
        setLibraryOpen(true);
        setLoadingTemplates(true);
        fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/templates`, { headers: routineHeaders() })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((json) => {
            const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
            setTemplates(list.map(mapApiRoutineTemplate));
        })
            .catch(() => setTemplates([]))
            .finally(() => setLoadingTemplates(false));
    }, []);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("new") === "1") {
            navigate(ROUTES.MOMENTUM_ROUTINES, { replace: true });
            setEditingRoutine(null);
            setPrefilledName("");
            setRoutineBuilderPrefill(null);
            setView("create");
        }
        else if (params.get("library") === "1") {
            navigate(ROUTES.MOMENTUM_ROUTINES, { replace: true });
            if (SHOW_ROUTINE_LIBRARY)
                handleOpenLibrary();
        }
    }, [location.search]);
    const handleUseTemplate = (template: RoutineTemplate) => {
        setEditingRoutine(null);
        setPrefilledName(template.name);
        setRoutineBuilderPrefill({
            description: template.description,
            prompt: template.prompt,
            skillIds: template.skillIds,
            skillNames: template.skillNames,
            assigneeAgentId: template.assigneeAgentId,
            assigneeAgentName: template.assigneeAgentName,
            scheduleType: template.scheduleType,
            scheduleTime: template.scheduleTime,
            scheduleTimeTz: template.scheduleTimeTz,
            scheduleDays: template.scheduleDays,
            scheduleDates: template.scheduleDates,
            scheduleOnceDate: template.scheduleOnceDate,
        });
        setView("create");
        setLibraryOpen(false);
    };
    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        searchRef.current = value;
        if (searchDebounceRef.current)
            clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setPage(1);
            fetchRoutines(1);
        }, 350);
    }, [fetchRoutines]);
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);
        };
    }, []);
    useEffect(() => {
        if (listDeferredRef.current)
            return;
        fetchRoutines(1);
    }, [fetchRoutines]);
    // Back on the list after a deep-linked routine: load what was deferred.
    useEffect(() => {
        if (listDeferredRef.current && !routinePathId)
            fetchRoutines(1);
    }, [routinePathId, fetchRoutines]);
    useEffect(() => {
        if (page > 1)
            fetchRoutines(page);
    }, [page, fetchRoutines]);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasMore || loadingMore)
            return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting)
                setPage((p) => p + 1);
        }, { threshold: 0.1 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loadingMore]);
    const handleSave = async (data: Omit<Routine, "id" | "createdAt">) => {
        setSaving(true);
        try {
            const payload = {
                id: editingRoutine ? editingRoutine.id : undefined,
                name: data.name,
                description: data.description,
                prompt: data.prompt,
                skillIds: data.skillIds,
                skillNames: data.skillNames,
                assigneeAgentId: data.assigneeAgentId || null,
                assigneeAgentName: data.assigneeAgentId ? data.assigneeAgentName : "Default AI Agent",
                scheduleType: normalizeScheduleType(data.scheduleType),
                scheduleTime: data.scheduleTime,
                scheduleTimeTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                scheduleDays: data.scheduleDays,
                scheduleDates: data.scheduleDates,
                scheduleOnceDate: data.scheduleOnceDate || null,
                intervalMinutes: data.scheduleType === "interval" ? (data.intervalMinutes ?? DEFAULT_INTERVAL_MINUTES) : null,
                isActive: data.isActive,
            };
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/upsert`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify(payload),
            });
            if (!res.ok)
                throw new Error("Save failed");
            const json = await res.json();
            if (editingRoutine) {
                setRoutines((prev) => prev.map((r) => (r.id === editingRoutine.id ? { ...r, ...data } : r)));
                showToast("success", "Routine updated successfully");
            }
            else {
                const saved = json?.data ?? json;
                const newRoutine: Routine = saved?.name
                    ? mapApiRoutine(saved)
                    : { ...data, id: String(saved?.id ?? json.id), createdAt: new Date().toISOString() };
                setRoutines((prev) => [newRoutine, ...prev]);
                showToast("success", "Routine created successfully");
            }
            setPage(1);
            fetchRoutines(1, true);
            refreshRoutineStats();
        }
        catch {
            showToast("error", "Failed to save routine. Please try again.");
        }
        finally {
            setSaving(false);
        }
        setView("list");
        setEditingRoutine(null);
        goToRoutinesList();
    };
    const handleDelete = async (id: string) => {
        setDeleteConfirm(null);
        setRoutines((prev) => prev.filter((r) => r.id !== id));
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${id}`, {
                method: "DELETE",
                headers: routineHeaders(),
            });
            if (!res.ok)
                throw new Error();
            showToast("success", "Routine deleted");
            refreshRoutineStats();
        }
        catch {
            fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/list`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify({ searchBy: searchRef.current.trim() || null }),
            })
                .then((r) => (r.ok ? r.json() : Promise.reject()))
                .then((json) => setRoutines((json?.data ?? []).map(mapApiRoutine)))
                .catch(() => { });
            showToast("error", "Failed to delete routine");
        }
    };
    const handleToggle = async (id: string) => {
        const routine = routines.find((r) => r.id === id);
        if (!routine)
            return;
        const newActive = !routine.isActive;
        setRoutines((prev) => prev.map((r) => (r.id === id ? { ...r, isActive: newActive } : r)));
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumRoutine/${id}/toggle`, {
                method: "PATCH",
                headers: routineHeaders(),
                body: JSON.stringify({ isActive: newActive }),
            });
            if (!res.ok)
                throw new Error();
            refreshRoutineStats();
            if (routine.skillNames.some((n) => n.toLowerCase().includes("rate quot"))) {
                fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/rate-quoting/settings/ai-auto-execute`, {
                    method: "PATCH",
                    headers: routineHeaders(),
                    body: JSON.stringify({ aiAutoExecute: newActive }),
                }).catch(() => { });
                const RATE_SETTINGS_KEY = "rateQuotingSkill.settings.v1";
                const isNewLead = routine.skillNames.some((n) => n.toLowerCase().includes("new lead"));
                const isRenewal = routine.skillNames.some((n) => n.toLowerCase().includes("renewal"));
                try {
                    const raw = localStorage.getItem(RATE_SETTINGS_KEY);
                    if (raw) {
                        const cur = JSON.parse(raw);
                        const updated = { ...cur };
                        if (isNewLead)
                            updated.newLead = { ...updated.newLead, enabled: newActive, aiAutoExecute: newActive };
                        if (isRenewal)
                            updated.renewal = { ...updated.renewal, enabled: newActive };
                        localStorage.setItem(RATE_SETTINGS_KEY, JSON.stringify(updated));
                        const authToken = localStorage.getItem("authToken");
                        if (authToken) {
                            fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/rate-quoting/settings`, {
                                method: "PUT",
                                headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "application/json" },
                                body: JSON.stringify(updated),
                            }).catch(() => { });
                        }
                    }
                }
                catch {
                }
            }
        }
        catch {
            setRoutines((prev) => prev.map((r) => (r.id === id ? { ...r, isActive: !newActive } : r)));
            showToast("error", "Failed to update routine status");
        }
    };
    const handleCreateRoutineFromInput = useCallback(async () => {
        const prompt = routineInput.trim();
        if (!prompt || buildingRoutine)
            return;
        setRoutineSuggestionsOpen(false);
        setRoutineInput("");
        setEditingRoutine(null);
        setPrefilledName(prompt);
        setRoutineBuilderPrefill(null);
        setView("create");
        setBuildingRoutine(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_MyAMP_Url || "")}/api/ams/routine-builder`, {
                method: "POST",
                headers: routineHeaders(),
                body: JSON.stringify({ prompt }),
            });
            if (!res.ok || !res.body)
                throw new Error("Routine builder request failed");
            let streamText = "";
            const done = { name: false, description: false, instruction: false, trigger: false };
            const applyMarkers = () => {
                if (!done.name) {
                    const m = extractRoutineMarker(streamText, "ROUTINE_NAME");
                    if (m) {
                        done.name = m.complete;
                        setPrefilledName(m.value || prompt);
                    }
                }
                if (!done.description) {
                    const m = extractRoutineMarker(streamText, "ROUTINE_DESCRIPTION");
                    if (m) {
                        done.description = m.complete;
                        setRoutineBuilderPrefill((prev) => ({ ...(prev || {}), description: m.value }));
                    }
                }
                if (!done.instruction) {
                    const m = extractRoutineMarker(streamText, "ROUTINE_INSTRUCTION");
                    if (m) {
                        done.instruction = m.complete;
                        setRoutineBuilderPrefill((prev) => ({ ...(prev || {}), prompt: m.value }));
                    }
                }
                if (!done.trigger) {
                    const m = extractRoutineMarker(streamText, "ROUTINE_TRIGGER");
                    if (m?.complete) {
                        done.trigger = true;
                        setRoutineBuilderPrefill((prev) => ({ ...(prev || {}), scheduleType: mapRoutineTrigger(m.value) }));
                    }
                }
            };
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let sseBuffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                sseBuffer += decoder.decode(value, { stream: true });
                const events = sseBuffer.split("\n\n");
                sseBuffer = events.pop() ?? "";
                for (const event of events) {
                    const dataLine = event
                        .split(/\r?\n/)
                        .map((l) => l.trim())
                        .find((l) => l.startsWith("data:"));
                    if (!dataLine)
                        continue;
                    let json: any = null;
                    try {
                        json = JSON.parse(dataLine.replace(/^data:\s*/, ""));
                    }
                    catch {
                        continue;
                    }
                    if (json?.errorMessage)
                        throw new Error(json.errorMessage);
                    if (typeof json?.response === "string") {
                        streamText += json.response;
                        applyMarkers();
                    }
                }
            }
            if (sseBuffer.trim()) {
                const dataLine = sseBuffer
                    .split(/\r?\n/)
                    .map((l) => l.trim())
                    .find((l) => l.startsWith("data:"));
                if (dataLine) {
                    try {
                        const json: any = JSON.parse(dataLine.replace(/^data:\s*/, ""));
                        if (typeof json?.response === "string") {
                            streamText += json.response;
                            applyMarkers();
                        }
                    }
                    catch {
                    }
                }
            }
            if (!done.name && !done.description && !done.instruction && !done.trigger) {
                throw new Error("Routine builder returned no routine fields");
            }
        }
        catch {
            setPrefilledName(prompt);
            setRoutineBuilderPrefill(null);
            showToast("error", "Could not auto-generate routine details. You can fill them in manually.");
        }
        finally {
            setBuildingRoutine(false);
        }
    }, [routineInput, buildingRoutine]);
    const refreshRoutineStats = useCallback(() => {
        getRoutineStats()
            .then(setRoutinesStats)
            .catch(() => { });
    }, [setRoutinesStats]);
    useEffect(() => {
        refreshRoutineStats();
    }, [refreshRoutineStats]);
    return (<div className="mr-page">
      <AnimatePresence mode="wait">
        {view === "detail" && viewingRoutine ? (<RoutineDetailPage key="detail" routine={viewingRoutine} autoExpandRunId={deepLinkRunId} onBack={() => {
                setView("list");
                setViewingRoutine(null);
                setDeepLinkRunId(null);
                goToRoutinesList();
            }} onEdit={() => {
                setEditingRoutine(viewingRoutine);
                setView("edit");
                setViewingRoutine(null);
                setDeepLinkRunId(null);
            }}/>) : view === "create" || view === "edit" ? (<RoutineForm key="form" initial={editingRoutine} initialName={!editingRoutine ? prefilledName : undefined} initialPrefill={!editingRoutine ? (routineBuilderPrefill ?? undefined) : undefined} dwAgents={dwAgents} generating={!editingRoutine && buildingRoutine} onSave={handleSave} onCancel={() => {
                setView("list");
                setEditingRoutine(null);
                setPrefilledName("");
                setRoutineBuilderPrefill(null);
                goToRoutinesList();
            }} onPreview={editingRoutine
                ? () => {
                    setViewingRoutine(editingRoutine);
                    setView("detail");
                    goToRoutineDetail(editingRoutine.id);
                }
                : undefined} saving={saving}/>) : (<motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            
            <div className="mr-detail-header" style={{ marginBottom: 20 }}>
              
              <div style={{ position: "relative", width: 280, flexShrink: 0 }}>
                <FiSearch size={14} style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-tertiary)",
                pointerEvents: "none",
            }}/>
                <input type="text" value={search} onChange={(e) => handleSearchChange(e.target.value)} placeholder="Search routines…" style={{
                width: "100%",
                padding: "6px 10px 6px 32px",
                borderRadius: 8,
                border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                background: isLight ? "#ffffff" : "rgba(255,255,255,0.03)",
                color: "var(--text-primary)",
                fontSize: 12.5,
                fontFamily: "inherit",
                outline: "none",
            }}/>
              </div>

              <div role="group" aria-label="Filter routines by status" style={{
                display: "inline-flex",
                padding: 2,
                gap: 2,
                borderRadius: 8,
                border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.08)",
                background: isLight ? "#ffffff" : "rgba(255,255,255,0.03)",
                flexShrink: 0,
            }}>
                {(["all", "active", "paused"] as const).map((key) => {
                const selected = statusFilter === key;
                return (<button key={key} type="button" aria-pressed={selected} onClick={() => setStatusFilter(key)} style={{
                        padding: "4px 12px",
                        borderRadius: 6,
                        border: "none",
                        background: selected ? "rgba(124,58,237,0.18)" : "transparent",
                        color: selected ? (isLight ? "#6d28d9" : "#c4b5fd") : "var(--text-secondary)",
                        fontSize: 12,
                        fontWeight: selected ? 600 : 500,
                        cursor: "pointer",
                        fontFamily: "inherit",
                    }}>
                      {key === "all" ? "All" : key === "active" ? "Active" : "Paused"}
                    </button>);
            })}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button className="mc-btn-icon" onClick={() => {
                setPage(1);
                fetchRoutines(1);
            }} disabled={loading} title="Refresh routines" style={{
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
            }}>
                  <FiRefreshCw size={14} style={loading ? { animation: "spin 1s linear infinite" } : undefined}/>
                </button>

                {SHOW_ROUTINE_LIBRARY && (<motion.button whileHover={{ scale: 1.02, boxShadow: "0 6px 24px rgba(124,58,237,0.55)" }} whileTap={{ scale: 0.97 }} onClick={() => (libraryOpen ? setLibraryOpen(false) : handleOpenLibrary())} style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 8,
                border: "1px solid rgba(139,92,246,0.4)",
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                color: "#fff",
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                boxShadow: "0 4px 16px rgba(124,58,237,0.4)",
            }}>
                  {libraryOpen ? (<>
                      <FiList size={11}/> My Routines
                    </>) : (<>
                      <FiBookmark size={11}/> Library
                    </>)}
                </motion.button>)}
                <motion.button whileHover={{ scale: 1.02, boxShadow: "0 6px 24px rgba(124,58,237,0.55)" }} whileTap={{ scale: 0.97 }} onClick={() => {
                setEditingRoutine(null);
                setPrefilledName("");
                setRoutineBuilderPrefill(null);
                setView("create");
            }} style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 8,
                border: "1px solid rgba(139,92,246,0.4)",
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                color: "#fff",
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                boxShadow: "0 4px 16px rgba(124,58,237,0.4)",
            }}>
                  <FiClock size={11}/> New Routine
                </motion.button>
              </div>
            </div>

            {libraryOpen ? (loadingTemplates ? (<div className="mr-library-grid">
                  {[1, 2, 3, 4, 5, 6].map((i) => (<div key={i} className="mr-library-card mr-library-card--skel">
                      <div className="mr-skel-bone" style={{ width: 64, height: 18, borderRadius: 6 }}/>
                      <div className="mr-skel-bone" style={{ width: "70%", height: 15 }}/>
                      <div className="mr-skel-bone" style={{ width: "100%", height: 11 }}/>
                      <div className="mr-skel-bone" style={{ width: "85%", height: 11 }}/>
                      <div className="mr-skel-bone" style={{ width: 110, height: 30, borderRadius: 8, marginTop: 4 }}/>
                    </div>))}
                </div>) : (<div className="mr-library-grid">
                {templates.length === 0 ? (<div style={{ padding: "32px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
                    No premade routines available yet.
                  </div>) : (templates.map((template) => (<div key={template.id} className="mr-library-card">
                      {template.category && <span className="mr-library-card__tag">{template.category}</span>}
                      <h4 className="mr-library-card__title">{template.name}</h4>
                      <p className="mr-library-card__desc">{template.description}</p>
                      <button className="mr-library-card__cta" onClick={() => handleUseTemplate(template)}>
                        Use this template
                      </button>
                    </div>)))}
              </div>)) : (<>
            
            <div className="cc-ai-task-row">
              <div className="cc-ai-task-input-bar">
                <div className="cc-ai-task-label">
                  <div className="cc-ai-task-dot">✦</div>
                  New Routine
                </div>
                <input className="cc-ai-input" placeholder="Name or describe your new routine…" value={routineInput} onChange={(e) => setRoutineInput(e.target.value)} onFocus={() => setRoutineSuggestionsOpen(true)} onKeyDown={(e) => {
                    if (e.key === "Enter")
                        handleCreateRoutineFromInput();
                }}/>
                <button className="cc-ai-assign-btn" onClick={handleCreateRoutineFromInput} disabled={!routineInput.trim() || buildingRoutine} aria-label={buildingRoutine ? "Creating routine" : "Create routine"}>
                  {buildingRoutine ? (<>
                      <FiRefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }}/>
                      Creating...
                    </>) : ("Create")}
                </button>
                <button className={`cc-ai-expand-btn${routineSuggestionsOpen ? " cc-ai-expand-btn--open" : ""}`} onClick={() => setRoutineSuggestionsOpen((p) => !p)}>
                  ▾
                </button>
              </div>
              <div className={`cc-ai-suggestions${routineSuggestionsOpen ? " cc-ai-suggestions--visible" : ""}`}>
                <div className="cc-ai-suggestions__label">Suggested routines</div>
                {ROUTINE_SUGGESTIONS.map((s, i) => (<div key={i} className="cc-ai-suggestion-item" onClick={() => {
                        setRoutineInput(s.text);
                        setRoutineSuggestionsOpen(false);
                    }}>
                    <div className="cc-ai-suggestion-dot"/>
                    <div className="cc-ai-suggestion-text">{s.text}</div>
                    <span className="cc-ai-suggestion-tag">{s.tag}</span>
                  </div>))}
              </div>
            </div>

            
            {loading ? (<div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                {[1, 2, 3, 4].map((i) => (<div key={i} className="mr-skel-card ">
                    <div className="mr-skel-bone" style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0 }}/>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 9 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="mr-skel-bone" style={{ width: "30%", height: 14 }}/>
                        <div className="mr-skel-bone" style={{ width: 62, height: 15, borderRadius: 99 }}/>
                      </div>
                      <div className="mr-skel-bone" style={{ width: "55%", height: 11 }}/>
                      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                        <div className="mr-skel-bone" style={{ width: 130, height: 10 }}/>
                        <div className="mr-skel-bone" style={{ width: 96, height: 10 }}/>
                        <div className="mr-skel-bone" style={{ width: 78, height: 10 }}/>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <div className="mr-skel-bone" style={{ width: 36, height: 20, borderRadius: 99 }}/>
                      <div className="mr-skel-bone" style={{ width: 58, height: 27, borderRadius: 8 }}/>
                      <div className="mr-skel-bone" style={{ width: 32, height: 27, borderRadius: 8 }}/>
                    </div>
                  </div>))}
              </div>) : routines.length === 0 ? (<div className="mr-empty-state">
                <div className="mr-empty-state__icon">
                  <FiRefreshCw size={28}/>
                </div>
                <h3 className="mr-empty-state__title">No routines yet</h3>
                <p className="mr-empty-state__message">Create your first routine to automate recurring tasks in your agency.</p>
                <button onClick={() => setView("create")} style={{
                        marginTop: 8,
                        padding: "10px 24px",
                        borderRadius: 10,
                        border: "none",
                        background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                    }}>
                  Create First Routine
                </button>
              </div>) : (<div className="mr-routines-list">
                {visibleRoutines.length === 0 && !hasMore && !loadingMore && (<div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
                    {statusFilter === "active" ? "No active routines." : "No paused routines."}
                  </div>)}
                <AnimatePresence>
                  {visibleRoutines.map((r) => (<RoutineCard key={r.id} routine={r} onView={() => {
                            setViewingRoutine(r);
                            setView("detail");
                            goToRoutineDetail(r.id);
                        }} onEdit={() => {
                            setEditingRoutine(r);
                            setView("edit");
                        }} onDelete={() => setDeleteConfirm(r.id)} onToggle={() => handleToggle(r.id)}/>))}
                </AnimatePresence>
                
                <div ref={sentinelRef} style={{ height: 1 }}/>
                {loadingMore && (<div style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "16px 0",
                            color: "rgba(255,255,255,0.4)",
                            fontSize: 13,
                            gap: 8,
                            alignItems: "center",
                        }}>
                    <FiRefreshCw size={13} style={{ animation: "spin 1s linear infinite" }}/>
                    Loading more…
                  </div>)}
              </div>)}
              </>)}
          </motion.div>)}
      </AnimatePresence>

      
      <AnimatePresence>
        {deleteConfirm && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{
                position: "fixed",
                inset: 0,
                background: isLight ? "rgba(15,23,42,0.25)" : "rgba(0,0,0,0.65)",
                backdropFilter: "blur(4px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
            }} onClick={() => setDeleteConfirm(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} onClick={(e) => e.stopPropagation()} style={{
                background: isLight ? "#ffffff" : "#1a1025",
                border: isLight ? "1px solid #e5e7eb" : "1px solid rgba(255,255,255,0.1)",
                borderRadius: 16,
                padding: "28px 32px",
                maxWidth: 400,
                width: "100%",
                margin: "0 16px",
            }}>
              <h3 style={{ color: isLight ? "#111827" : "rgba(255,255,255,0.9)", fontSize: 16, fontWeight: 700, margin: "0 0 10px" }}>
                Delete Routine?
              </h3>
              <p style={{ color: isLight ? "#6b7280" : "rgba(255,255,255,0.45)", fontSize: 13, lineHeight: 1.6, margin: "0 0 24px" }}>
                Are you sure you want to delete{" "}
                <strong style={{ color: isLight ? "#374151" : "rgba(255,255,255,0.75)" }}>
                  {routines.find((r) => r.id === deleteConfirm)?.name}
                </strong>
                ? This cannot be undone.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setDeleteConfirm(null)} style={{
                flex: 1,
                padding: "10px",
                borderRadius: 9,
                border: isLight ? "1px solid #d1d5db" : "1px solid rgba(255,255,255,0.1)",
                background: "transparent",
                color: isLight ? "#4b5563" : "rgba(255,255,255,0.55)",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
            }}>
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} style={{
                flex: 1,
                padding: "10px",
                borderRadius: 9,
                border: "none",
                background: "linear-gradient(135deg, #dc2626, #b91c1c)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
            }}>
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>)}
      </AnimatePresence>

      
      <AnimatePresence>
        {toast && (<motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className={toast.type === "success" ? "success-toast" : "error-toast"}>
            {toast.type === "success" ? <FiCheck size={15}/> : <FiAlertTriangle size={15}/>}
            <span>{toast.msg}</span>
          </motion.div>)}
      </AnimatePresence>

      
    </div>);
};
export default MomentumRoutinesPage;
